import { Dispatch, ReactNode, SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { config, isLiveApi } from "./config";
import homeLogo from "./assets/safeguard-global-jobs-logo.png";
import { t, tFor, useLocale, browserLocaleBase, hasStoredLocale, setLocale, LANGUAGE_NAMES, SUPPORTED_LOCALES, type Locale, type TKey,
  countryLabel,
} from "./i18n";
import {
  agencyPortalUrl,
  confirmContactCode,
  getTelegramBindingStatus,
  getChatbotBindingStatus,
  fetchAgencies,
  PasswordLinkRefusedError,
  fetchSiteChatbotConfig,
  fetchAgencyWizardData,
  fetchRegistrationDocumentTypes,
  fetchScreeningQuestions,
  registerCandidate,
  requestContactCode,
  requestTelegramBinding,
  requestWhatsAppLink,
  resendPasswordLink,
  resendTelegramPhoneRequest,
  screeningDocumentName,
  uploadRegistrationDocument,
  type JobTypeDTO,
  type LoadedAgencyWizard,
  type PasswordMailStatus,
  type RegionDTO,
  type RegistrationDocumentTypeDTO,
  type SiteChatbotConfigDTO,
  type StaffingAgencyDTO,
  type StaffingScreeningQuestionDTO,
  type WorkCategoryDTO,
} from "./api";

// T-SA-23.05 (2026-10-22): the terms of use are a wizard step — after jobs and regions, before the
// personal data.
const STEPS = ["jobs", "regions", "terms", "about"] as const;
// The landing's "Register here" opens the funnel at "jobs" (full width); Back from "jobs" returns
// to the landing (input kept).
const FIRST_FUNNEL_STEP = STEPS.indexOf("jobs");

/** The step bar's steps: the 4 wizard steps + the post-submit contact confirmation. */
const FLOW_STEPS = ["jobs", "regions", "terms", "about", "verify"] as const;
const FLOW_STEP_LABELS: Record<(typeof FLOW_STEPS)[number], TKey> = {
  jobs: "steps.jobs",
  regions: "steps.regions",
  terms: "steps.terms",
  about: "steps.about",
  verify: "steps.verify",
};
// The COUNTRY step is SKIPPED (2026-10-02): the selected agency's contract carries its
// activity country — the countries/regions data arrive from the wizard endpoint already
// scoped; the country is mentioned on the regions step as "Operates in <country>".

/** StaffingCandidateCandidateContactChannel (server enum): Email = 0, WhatsApp = 1 (PRD §7). */
const ContactChannels = { Email: 0, WhatsApp: 1 } as const;

interface Registration {
  wantedJobs: string[]; // job-type Ids (scoped to the selected agency)
  /** One single country (2026-09-30 decision) — CountryCode, proposed by the selected agency. */
  country: string;
  /** Server-provided regions for the selected country (Region Ids). */
  regions: string[];
  fullName: string;
  birthDate: string;
  /** PRD §24.7 — "" until picked; else the server enum value ("0" Male, "1" Female, "2" prefer not to disclose). */
  gender: "" | "0" | "1" | "2";
  taxId: string;
  whatsapp: string;
  whatsappOptIn: boolean;
  email: string;
  /** "I agree to the Terms of Use and Privacy Policy" (T-SA-19.01) — T-SA-23.05: moved from the
   *  landing into the wizard's terms step; gates its Continue. */
  termsAgreed: boolean;
  /** Chatbot PRD §3/§7: the candidate's PRIMARY chatbot channel - WhatsApp (default) or
   *  Telegram. Telegram skips the opt-in checkbox and shows the /start link-Telegram
   *  state on the verify step (no number needed - binding proves ownership). */
  chatbot: "whatsapp" | "telegram";
  /** Screening answers, keyed by question Id: yes/no questions store "yes" | "no",
   * text questions store the free text (PRD §4.11 answer types), list questions the picked option Id (§21.6). */
  answers: Record<string, string>;
  /** Attached files for doc-type questions, keyed by question Id (sent to the server). */
  files: Record<string, File>;
  /** Optional agency-requested sign-up documents (PRD §4.12), keyed by document-type Id. */
  agencyDocs: Record<string, File>;
}

const initial: Registration = {
  wantedJobs: [],
  country: "",
  regions: [],
  fullName: "",
  birthDate: "",
  gender: "",
  taxId: "",
  whatsapp: "",
  whatsappOptIn: false,
  email: "",
  termsAgreed: false,
  chatbot: "whatsapp",
  answers: {},
  files: {},
  agencyDocs: {},
};

let toastTimer: number | undefined;
function toast(msg: string) {
  const t = document.getElementById("toast");
  if (!t) return;
  t.textContent = msg;
  t.classList.add("on");
  clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => t.classList.remove("on"), 2600);
}

type Phase = "apply" | "verify" | "done";

/**
 * Local record of a successful registration (2026-09-30; the per-agency + name fields 2026-10-08):
 * kept in localStorage, used for the contact verifications — a returning candidate on the same
 * browser can complete (or re-run) the WhatsApp (primary) and optional e-mail verifications via
 * the "Verify me" header link, WHICH IS SCOPED TO THE SELECTED AGENCY now (the record carries the
 * agency id + the candidate name for the "Verify me — First L." display).
 * Cleared on every new submit click (another candidate may use the same page/device) BEFORE any
 * server request is sent.
 */
const LS_SAVED_CANDIDATE_ID = "staffing.savedCandidateId";
const LS_CHATBOT = "staffing.chatbot";
const LS_TELEGRAM_TOKEN = "staffing.telegramToken";
const LS_REGISTERED = "staffing.registered";
const LS_WHATSAPP = "staffing.whatsapp";
const LS_EMAIL = "staffing.email";
const LS_AGENCY_ID = "staffing.agencyId";
const LS_FULL_NAME = "staffing.fullName";

export interface StoredRegistration {
  candidateId: string;
  whatsapp: string;
  email: string;
  chatbot: "whatsapp" | "telegram";
  telegramToken: string;
  /** The agency the candidate registered WITH — "Verify me" shows only for it (2026-10-08). */
  staffingAgencyId: string;
  /** The registration's FULL NAME (the "Verify me — Juan G." display). */
  fullName: string;
}

function readStoredRegistration(): StoredRegistration | null {
  const candidateId = localStorage.getItem(LS_SAVED_CANDIDATE_ID) ?? "";
  const whatsapp = localStorage.getItem(LS_WHATSAPP) ?? "";
  const email = localStorage.getItem(LS_EMAIL) ?? "";
  const chatbot = (localStorage.getItem(LS_CHATBOT) as "whatsapp" | "telegram") ?? "whatsapp";
  const telegramToken = localStorage.getItem(LS_TELEGRAM_TOKEN) ?? "";
  const staffingAgencyId = localStorage.getItem(LS_AGENCY_ID) ?? "";
  const fullName = localStorage.getItem(LS_FULL_NAME) ?? "";
  return localStorage.getItem(LS_REGISTERED) === "true" && candidateId && (whatsapp || chatbot === "telegram")
    ? { candidateId, whatsapp, email, chatbot, telegramToken, staffingAgencyId, fullName }
    : null;
}

function clearStoredRegistration() {
  localStorage.removeItem(LS_SAVED_CANDIDATE_ID);
  localStorage.removeItem(LS_REGISTERED);
  localStorage.removeItem(LS_WHATSAPP);
  localStorage.removeItem(LS_EMAIL);
  localStorage.removeItem(LS_CHATBOT);
  localStorage.removeItem(LS_TELEGRAM_TOKEN);
  localStorage.removeItem(LS_AGENCY_ID);
  localStorage.removeItem(LS_FULL_NAME);
}

function storeRegistration(
  savedCandidateId: string,
  whatsapp: string,
  email: string,
  chatbot: "whatsapp" | "telegram" = "whatsapp",
  telegramToken = "",
  staffingAgencyId = "",
  fullName = ""
) {
  localStorage.setItem(LS_SAVED_CANDIDATE_ID, savedCandidateId);
  localStorage.setItem(LS_WHATSAPP, whatsapp);
  localStorage.setItem(LS_EMAIL, email);
  localStorage.setItem(LS_CHATBOT, chatbot);
  localStorage.setItem(LS_TELEGRAM_TOKEN, telegramToken);
  localStorage.setItem(LS_AGENCY_ID, staffingAgencyId);
  localStorage.setItem(LS_FULL_NAME, fullName);
  localStorage.setItem(LS_REGISTERED, "true");
}

export default function App() {
  // Subscribe to language switches — a picker change re-renders the whole page.
  const locale = useLocale();
  const [phase, setPhase] = useState<Phase>("apply");
  const [onFunnel, setOnFunnel] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [reg, setReg] = useState<Registration>(initial);
  const [err, setErr] = useState("");

  // The SELECTED AGENCY'S wizard data — loaded from the agency's CONTRACT via the
  // anonymous wizard endpoint (the system dictionaries are admin tables, 2026-10-02),
  // re-loaded whenever the selection changes.
  const [wizard, setWizard] = useState<LoadedAgencyWizard | null>(null);
  const [wizardError, setWizardError] = useState<string | null>(null);
  const [wizardTick, setWizardTick] = useState(0);

  const [questions, setQuestions] = useState<StaffingScreeningQuestionDTO[]>([]);
  const [docTypes, setDocTypes] = useState<RegistrationDocumentTypeDTO[]>([]);

  const [candidateId, setCandidateId] = useState<string | null>(null);
  /** The active Telegram binding token (chatbot PRD §5.2) - held for the verify-step polling. */
  const [telegramToken, setTelegramToken] = useState<string | null>(null);
  /** The registration SUBMIT in-flight (the spinner on the About step's Submit button). */
  const [submitting, setSubmitting] = useState(false);
  // the CHECK-STATUS intent: the verify step renders the confirm-email card ONLY (2026-10-07)
  const [statusIntent, setStatusIntent] = useState(false);
  // 2026-10 fix: the Log-in "Resend password link" button requests the resend FIRST and only
  // a server SUCCESS (Sent) navigates to the verify section — the outcome is seeded there.
  const [passwordLinkBusy, setPasswordLinkBusy] = useState(false);
  const [seedPasswordMail, setSeedPasswordMail] = useState<PasswordMailStatus | null>(null);
  /** Registration stored on this browser (successful save) — shows the "Verify me" header link
   *  SCOPED TO THE SELECTED AGENCY (storedReg.staffingAgencyId === the selected agency, 2026-10-08). */
  const [storedReg, setStoredReg] = useState<StoredRegistration | null>(() =>
    readStoredRegistration()
  );
  /** The STATUS-LINKS gating (2026-10-08): true = confirmed e-mail with the selected agency → the
   *  links enabled; false = the links DISABLED + the "register and confirm your account" hint;
   *  null = still probing. */
  const [statusLinksEnabled, setStatusLinksEnabled] = useState<boolean | null>(null);
  // 2026-10-08: which messenger the candidate actually LINKED (drives the done step's
  // reach-out wording: Telegram when linked, WhatsApp when the number was confirmed;
  // null = neither confirmed yet → the neutral wording)
  const [doneMessenger, setDoneMessenger] = useState<"telegram" | "whatsapp" | null>(null);

  // Agencies for this site (server lookup by PublicProviderSite host, PRD §5.1) and the
  // currently selected one — the candidate applies to exactly ONE agency (§13 row 43).
  const [agencies, setAgencies] = useState<StaffingAgencyDTO[]>([]);
  const [agenciesLoaded, setAgenciesLoaded] = useState(false);
  const [selectedAgency, setSelectedAgency] = useState<StaffingAgencyDTO | null>(null);
  // The deployment's PUBLIC chatbot config from the SERVER (chatbot PRD §5.2/§7, 2026-10-03
  // ruling): the /start deep link is built from this, the VITE_TELEGRAM_BOT_USERNAME env var
  // is only a FALLBACK for an older server (before GetSiteChatbotConfig was deployed).
  const [siteChatbot, setSiteChatbot] = useState<SiteChatbotConfigDTO | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetchSiteChatbotConfig().then((c) => {
      if (!cancelled) setSiteChatbot(c);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  /** REGISTERED WITH THE SELECTED AGENCY (2026-10-08) — the "Verify me" visibility rule. */
  const registeredWithSelectedAgency =
    !!storedReg && !!selectedAgency && storedReg.staffingAgencyId === selectedAgency.Id;

  // The STATUS-LINKS GATE (2026-10-08): a per-agency, per-registration probe — the links are
  // enabled only when the candidate IS registered with the SELECTED agency AND that
  // registration's e-mail is confirmed; otherwise disabled + the "register and confirm your
  // account to log in" hint (the hint renders in the header/under the done CTA).
  useEffect(() => {
    let cancelled = false;
    if (!registeredWithSelectedAgency || !storedReg) {
      if (!cancelled) setStatusLinksEnabled(false);
      return;
    }
    setStatusLinksEnabled(null); // probing
    getTelegramBindingStatus(storedReg.candidateId, "")
      .then((probe) => {
        if (cancelled) return;
        // the known candidate → the e-mail flag; an unknown candidate (ghost) → disabled
        setStatusLinksEnabled(!!probe && probe.EmailValidated === true);
      })
      .catch(() => {
        if (!cancelled) setStatusLinksEnabled(null); // the probe failed → no strong claim
      });
    return () => {
      cancelled = true;
    };
  }, [registeredWithSelectedAgency, storedReg, phase]);

  /** The effective bot @handle (no @): server payload first, env fallback second. */
  const telegramBotUsername = siteChatbot?.TelegramBotUsername || config.telegramBotUsername;
  /** A dead channel (server-disabled or no handle anywhere) hides the Telegram choice. */
  const telegramAvailable =
    (siteChatbot ? !!siteChatbot.TelegramEnabled : !siteChatbot) && !!telegramBotUsername;
  /** THE INBOUND NUMBER (chatbot PRD §15/16 — T-SA-18.18): THE WABA SENDER digits the CANDIDATES
   *  message — the wa.me handshake identity is the AGENCY'S CHATBOT number, never the candidate's
   *  personal one; server payload first (WhatsAppInboundNumber), env fallback second. */
  const whatsappInboundNumber =
    ((siteChatbot?.WhatsAppInboundNumber || config.whatsappInboundNumber || "").replace(/\D/g, "") || "");

  useEffect(() => {
    fetchAgencies()
      .then((list) => {
        setAgencies(list);
        // Restore a previously selected agency (same deployment) across refreshes.
        const savedId = localStorage.getItem("staffing.selectedAgencyId");
        const found = savedId ? list.find((a: StaffingAgencyDTO) => a.Id === savedId) : undefined;
        // With exactly ONE agency on this deployment, auto-select it immediately (2026-10-02):
        // there is nothing else to choose from — the user is not forced to click the single
        // card; "Apply now" is enabled straight away (no confusion, no stale persisted picks).
        if (found) setSelectedAgency(found);
        else {
          // Stored selection is stale/absent (matches none of the available agencies) — clear
          // the stale record always (2026-10-04), so a re-deployment cannot resurrect a wrong
          // agency; only when EXACTLY one agency is available, select it — otherwise nothing.
          if (savedId) localStorage.removeItem("staffing.selectedAgencyId");
          if (!selectedAgency && list.length === 1) {
            setSelectedAgency(list[0]);
            const onlyId = list[0]?.Id;
            if (onlyId) localStorage.setItem("staffing.selectedAgencyId", onlyId);
          }
        }
      })
      .finally(() => setAgenciesLoaded(true));
  }, []);

  const loadWizardData = (agencyId: string) => {
    let cancelled = false;
    // a language switch refetches the same agency's names: keep the data on screen meanwhile
    setWizard((prev) => (prev && prev.agencyId === agencyId ? prev : null));
    setWizardError(null);
    fetchAgencyWizardData(agencyId)
      .then((d) => {
        if (!cancelled) setWizard(d);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        // Live failures surface an error + retry — the wizard never runs on fabricated data
        // (a submission against fake job/region ids would be stored server-side).
        setWizardError(e instanceof Error ? e.message : t("toast.dictLoadFailed"));
      });
    return () => {
      cancelled = true;
    };
  };

  useEffect(() => {
    if (!selectedAgency || !selectedAgency.Id) {
      if (!selectedAgency) {
        setWizard(null);
        setWizardError(null);
      }
      return;
    }
    return loadWizardData(selectedAgency.Id);
  }, [selectedAgency, wizardTick, locale]);

  // The country of the application (the STO's CountryOfResidence) = the agency's activity
  // country — set as soon as the wizard data arrives (the country STEP is skipped).
  useEffect(() => {
    if (wizard?.activityCountry?.code) {
      setReg((r) => (r.country === wizard.activityCountry!.code ? r : { ...r, country: wizard.activityCountry!.code }));
    }
  }, [wizard?.activityCountry?.code]);

  /** The wizard's country option = the selected agency's activity country (its contract);
      without one, the countries derive from the regions the wizard data carries. */
  const agencyCountryCode = wizard?.activityCountry?.code ?? (selectedAgency?.ActivityCountry?.Code ?? "").trim();
  const countryOptions = wizard?.activityCountry
    ? [{ code: wizard.activityCountry.code, label: countryLabel(wizard.activityCountry.code, wizard.activityCountry.label) }]
    : agencyCountryCode
      ? [{ code: agencyCountryCode, label: countryLabel(agencyCountryCode, selectedAgency?.ActivityCountry?.Name) }]
      : [];

  const pickAgency = (agency: StaffingAgencyDTO) => {
    if (selectedAgency?.Id && selectedAgency.Id === agency.Id) return;
    setSelectedAgency(agency);
    if (agency.Id) localStorage.setItem("staffing.selectedAgencyId", agency.Id);
    else localStorage.removeItem("staffing.selectedAgencyId");
    // The wizard scope changes with the agency: discard any in-progress registration input.
    setReg({ ...initial });
    setErr("");
    if (agencyCountryCodeFor(agency)) setReg((r) => ({ ...r, country: agencyCountryCodeFor(agency) }));
    toast(t("toast.selected", { name: agency.Name }));
  };

  /**
   * "Check my status" / "Already registered" target (PRD §5.1, §13 row 43 — 2026-10-02):
   * the selected agency's **InternalProviderSite** (the big-platform sign-in site that
   * agency's contract points to). PublicProviderSite is this presentation site itself —
   * never the target. VITE_AGENCY_PORTAL_URL remains the deployment fallback only.
   */
  const portalUrl = agencyPortalUrl(selectedAgency);
  /** The DISPLAY form of the portal link: the same address, without the scheme (2026-10-02:
   * the shown link = where "Check my status leads"). */

  const portalDisplay = portalUrl.replace(/^https?:\/\//i, "");

  // Screening questions + the agency's requested documents load per (selected agency, country).
  useEffect(() => {
    if (!selectedAgency?.Id || !reg.country) {
      setQuestions([]);
      setDocTypes([]);
      return;
    }
    let cancelled = false;
    const agencyId = selectedAgency.Id;
    void Promise.all([
      fetchScreeningQuestions(agencyId, reg.country),
      fetchRegistrationDocumentTypes(agencyId),
    ])
      .then(([qs, dts]) => {
        if (cancelled) return;
        setQuestions(qs);
        setDocTypes(dts);
      })
      .catch(() => {
        if (cancelled) return;
        setQuestions([]);
        setDocTypes([]);
        toast(t("toast.questionsUnavailable"));
      });
    return () => {
      cancelled = true;
    };
  }, [selectedAgency, reg.country, locale]);

  /** "Shown to" scope (PRD §4.11, 2026-10-01; several job types 2026-10-20; several categories
   * 2026-10-21): no category = all applicants; otherwise one of its categories matches the
   * candidate's wanted jobs — a category narrowed to job types by one of them, a whole category by
   * any job of it (same rule as the server). Validated AND rendered from this same list. */
  const pickedCategories = new Set(
    (wizard?.jobTypes ?? [])
      .filter((j) => reg.wantedJobs.includes(j.Id))
      .map((j) => j.WorkCategoryId)
  );
  const visibleQuestions = questions.filter((q) => {
    const categoryIds = (q.WorkCategories ?? []).map((c) => (c?.Id ?? "").trim()).filter(Boolean);
    if (!categoryIds.length) return true;
    return categoryIds.some((categoryId) => {
      const jobTypeIds = (q.JobTypes ?? [])
        .filter((j) => j?.WorkCategoryId === categoryId)
        .map((j) => (j?.Id ?? "").trim())
        .filter(Boolean);
      return jobTypeIds.length
        ? jobTypeIds.some((id) => reg.wantedJobs.includes(id))
        : pickedCategories.has(categoryId);
    });
  });

  /** The landing's "Register here" (T-SA-23.05): opens the wizard at the jobs step straight away —
   *  the terms checkbox lives on the wizard's terms step. */
  const start = () => {
    if (!selectedAgency) return; // gated by the disabled button
    setErr("");
    setPhase("apply");
    setOnFunnel(true); // the agency selection is now locked until Cancel/finish
    if (agencyCountryCode) setReg((r) => ({ ...r, country: agencyCountryCode }));
    setStepIdx(FIRST_FUNNEL_STEP);
    window.scrollTo({ top: 0 });
  };

  /** Back to the landing WITHOUT discarding the input. */
  const backToLanding = () => {
    setErr("");
    setOnFunnel(false);
    window.scrollTo({ top: 0 });
  };

  /** The wizard cancel (2026-10-02): abandons the registration → back to the landing. */
  const cancel = () => {
    setReg({ ...initial });
    setErr("");
    setOnFunnel(false);
    setPhase("apply");
    setStepIdx(0);
    window.scrollTo({ top: 0 });
  };

  /** The workflow (after "Select jobs") takes the full width of the card (2026-10-21). */
  const inWorkflow = phase !== "apply" || onFunnel;

  /** The HOME button (top bar left + the done screen): back to the landing from ANY phase. */
  const goHome = cancel;

  const next = () => {
    setErr("");
    const step = STEPS[stepIdx];
    if (step === "jobs" && reg.wantedJobs.length === 0) return setErr(t("jobs.err"));
    if (step === "regions" && reg.regions.length === 0) return setErr(t("regions.err"));
    if (step === "terms" && !reg.termsAgreed) return setErr(t("terms.err"));
    setStepIdx((i) => Math.min(STEPS.length - 1, i + 1));
    window.scrollTo({ top: 0 });
  };

  const back = () => {
    if (stepIdx <= FIRST_FUNNEL_STEP) return backToLanding();
    setErr("");
    setStepIdx((i) => i - 1);
  };

  /** The step bar's jump to a FINISHED step (before the submit only). */
  const jumpToStep = (flowIdx: number) => {
    setErr("");
    setStepIdx(flowIdx);
    window.scrollTo({ top: 0 });
  };

  const submit = async () => {
    // A new submission is a NEW candidate: clear any locally-stored registration
    // from a previous candidate BEFORE anything is sent to the server (2026-09-30).
    clearStoredRegistration();
    setStoredReg(null);
    // THE AGENCY IS MANDATORY (2026-10-27): the server never substitutes an agency, so the page
    // must always send the one the candidate is applying to - a reload/restored funnel without a
    // resolvable selection is stopped here instead of posting an agency-less registration.
    if (!selectedAgency?.Id) return setErr(t("submit.err.selectAgency"));
    const missing = aboutFormError(reg, visibleQuestions);
    if (missing) return setErr(t(missing));
    const firstName = reg.fullName.trim().split(/\s+/)[0] ?? "";
    const lastName = reg.fullName.trim().split(/\s+/).slice(1).join(" ");
    setErr("");
    setSubmitting(true); // the spinner (2026-10-08): the submit waits for the server — visible feedback
    // 2026-09-30 ruling: the UI creates the candidate UUID and passes it in the STO —
    // the server does not modify the STO. On success, savedCandidateId is stored in
    // localStorage and used only for the contact confirmations (PRD §7).
    const candidateId = crypto.randomUUID();
    try {
      const result = await registerCandidate(
        {
          Id: candidateId,
          // The ONE agency selected on the landing page — the server validates it against the
          // calling site host (PublicProviderSite) and registers for that agency (PRD §4.10).
          StaffingAgencyId: selectedAgency.Id, // required: the agency the candidate applies to
          FirstName: firstName,
          LastName: lastName,
          BirthDate: reg.birthDate,
          Gender: Number(reg.gender),
          TaxId: reg.taxId.trim(),
          CountryOfResidence: reg.country,
          EmailAddress: reg.email.trim().toLowerCase(),
          WhatsAppNumber: reg.whatsapp.trim(),
          // Chatbot PRD §3: the candidate's PRIMARY chatbot channel rides with the registration
          // (Telegram=2 skips the opt-in bureaucracy; the /start binding lands on the verify step).
          PrimaryChatbotChannel: reg.chatbot === "telegram" ? 2 : 1,
          ProfileStatus: 0, // Pending — PRD §6.1
          SourceType: 2, // SelfRegistered
          WantedJobs: reg.wantedJobs.map((Id) => ({ Id })),
          ActivityRegions: reg.regions.map((Id) => ({ Id })),
          ScreeningAnswers: visibleQuestions.map((q) => {
            // a list question sends the picked option and its shown text (the server checks the option)
            if (q.AnswerType === 3) {
              const option = q.Options?.find((o) => o.Id === reg.answers[q.Id]);
              return { QuestionId: q.Id, AnswerText: option?.Text ?? "", SelectedOptionId: option?.Id ?? null };
            }
            return {
              QuestionId: q.Id,
              // doc-type questions are answered with the attached file: its name is recorded now,
              // the content is uploaded right after registration (SaveRegistrationDocumentFileAsync);
              // yes/no questions store "yes"/"no", text questions store the free text (PRD §4.11)
              AnswerText: isDocumentQuestion(q) ? `file: ${reg.files[q.Id]?.name ?? "pending"}` : reg.answers[q.Id] ?? "",
            };
          }),
        },
        { duplicateAgency: selectedAgency?.Name ?? config.agencyName }
      );
      if (!result.candidateId) return; // defensive: the UI always knows its own generated Id
      setCandidateId(result.candidateId);
      // Upload attached files (doc-type screening questions AND the agency's optional
      // requested documents — PRD 5.1/4.12, 2026-09-30/2026-10-02): each as its own document
      // — content stored server-side + a PendingReview flow row.
      // The candidate is registered by now: a failed upload (e.g. the file server is down) must not stop the
      // confirmations - it is counted, reported once, and the document can be added later from the profile.
      let failedUploads = 0;
      const uploadNonFatal = async (...args: Parameters<typeof uploadRegistrationDocument>) => {
        try {
          await uploadRegistrationDocument(...args);
        } catch (uploadError) {
          failedUploads++;
          console.warn(uploadError);
        }
      };
      for (const [questionId, file] of Object.entries(reg.files)) {
        const question = visibleQuestions.find((q) => q.Id === questionId);
        await uploadNonFatal(
          result.candidateId,
          selectedAgency?.Id ?? "",
          // prefer the system document type (the contract side records documents per type)
          screeningDocumentName(question),
          file,
          questionId
        );
      }
      for (const [docTypeId, file] of Object.entries(reg.agencyDocs)) {
        const docType = docTypes.find((d) => d.Id === docTypeId);
        await uploadNonFatal(
          result.candidateId,
          selectedAgency?.Id ?? "",
          docType?.DocumentName ?? docTypeId,
          file
        );
      }
      // Chatbot PRD §5.2: a Telegram-primary candidate gets the /start deep-link binding on the
      // verify step - request the binding token right away (the row lives 30 minutes).
      let bindingToken: string | null = null;
      if (reg.chatbot === "telegram") {
        // The wizard GENERATES the token (2026-09-30 convention - the save returns no data)
        // and passes it in the STO: it is known for the deep link + the status polling.
        bindingToken = await requestTelegramBinding(result.candidateId);
        setTelegramToken(bindingToken);
      }
      storeRegistration(
        result.candidateId,
        reg.whatsapp.trim(),
        reg.email.trim().toLowerCase(),
        reg.chatbot,
        bindingToken ?? "",
        selectedAgency?.Id ?? "",
        reg.fullName.trim()
      );
      setStoredReg(readStoredRegistration());
      // 2026-10-08: the channel-neutral toast (the old one hard-named WhatsApp even when the
      // candidate selected Telegram)
      toast(
        failedUploads
          ? t("verify.toast.registeredUploadsFailed", { count: String(failedUploads) })
          : t("verify.toast.registered")
      );
      setPhase("verify");
      window.scrollTo({ top: 0 });
    } catch (e) {
      setErr(e instanceof Error ? e.message : t("submit.err.failed"));
    } finally {
      setSubmitting(false);
    }
  };

  const stepProps = {
    reg,
    setReg,
    err,
    submitting,
    /** Chatbot PRD §7 (2026-10-03): the Telegram choice renders only when the deployment
     * carries a configured bot — server-provided handle or the env fallback. */
    telegramAvailable,
  };

  // The GATED STATUS LINKS (2026-10-07): BOTH the "Already registered?" header link and the
  // done screen's "Check my status" button first PROBE the candidate (token-less) — when the
  // e-mail is NOT confirmed yet, the links show the CONFIRM-EMAIL step (the account + the
  // password-reset link happen there) instead of redirecting to the internal site; a confirmed
  // e-mail redirects as before. A failing probe keeps the old redirect (best-effort).

  const openStatusLink = async () => {
    // 2026-10-08 (per Nicolae): the links ALWAYS WORK with a selected agency — the click
    // redirects to the agency portal for the candidate login; the probing below is
    // fire-and-forget ONLY for the warning hints (the ghost cleanup etc.), never blocking.
    window.open(portalUrl, "_blank", "noopener,noreferrer");
    const candidate = storedReg?.candidateId;
    if (!candidate) return;
    try {
      const probe = await getTelegramBindingStatus(candidate, "");
      // the unknown candidate: clear the stale local record (the ghost cleanup)
      if (probe && !probe.Status) {
        clearStoredRegistration();
        setStoredReg(null);
        toast(t("header.staleRegistration"));
        goHome();
      }
    } catch {
      // the probe is best-effort; the redirect already happened
    }
  };

  /** The confirmed e-mail with the selected agency (2026-10-09 success state) — the alert shows on
   *  the landing and the wizard steps (the right-panel top, T-SA-19.01). */
  const showConfirmedNotice =
    agencies.length > 0 && !!selectedAgency && !!storedReg && registeredWithSelectedAgency
    && statusLinksEnabled === true && phase === "apply";

  /** 2026-10-08: "Forget my data" — clears the per-agency saved registration (localStorage). */
  const forgetMyData = () => {
    clearStoredRegistration();
    setStoredReg(null);
    toast(t("header.forgotToast"));
    goHome();
  };

  /** T-SA-18.14: the confirmed e-mail's password-link step. 2026-10 fix: the button requests
   *  the resend FIRST; the jump to the confirm view (the "Verify me" section) happens only on
   *  a server SUCCESS — the refusals (NoInternalSite / SendFailed) and errors surface as a
   *  toast where the candidate clicked, the view stays. */
  const openPasswordLink = async () => {
    if (!storedReg) return;
    const email = storedReg.email || "";
    setPasswordLinkBusy(true);
    try {
      const mailStatus = await resendPasswordLink(storedReg.candidateId, email);
      if (mailStatus !== "Sent") {
        toast(
          mailStatus === "NoInternalSite"
            ? t("email.password.notSent.noSite")
            : mailStatus === "SendFailed"
              ? t("email.password.notSent.failed", { site: portalDisplay || t("email.password.signInPage") })
              : t("email.password.err.resend"));
        return;
      }
      setSeedPasswordMail(mailStatus);
      setCandidateId(storedReg.candidateId);
      setReg((r) => ({ ...r, whatsapp: storedReg.whatsapp, email: storedReg.email || r.email, chatbot: storedReg.chatbot }));
      setTelegramToken(storedReg.telegramToken || null);
      setStatusIntent(true); // the e-mail focused view: confirmed state + the password link
      setPhase("verify");
      toast(t("email.password.toast.resent", { email: email || t("about.email") }));
      window.scrollTo({ top: 0 });
    } catch (e) {
      // 2026-10 fix (feedback): an ANSWERED refusal is still an answer — "Password already
      // set" means the account is usable, the mail is not needed; the app still switches
      // to the verify/confirm section (the view shows the confirmed state there).
      if (e instanceof PasswordLinkRefusedError && e.status === "PasswordAlreadySet") {
        setCandidateId(storedReg.candidateId);
        setReg((r) => ({ ...r, whatsapp: storedReg.whatsapp, email: storedReg.email || r.email, chatbot: storedReg.chatbot }));
        setTelegramToken(storedReg.telegramToken || null);
        setStatusIntent(true);
        setPhase("verify");
        toast(e.message);
        window.scrollTo({ top: 0 });
        return;
      }
      toast(e instanceof Error ? e.message : t("email.password.err.resend"));
    } finally {
      setPasswordLinkBusy(false);
    }
  };

  return (
    <>
      <Header
        onCheckStatus={() => {
          void openStatusLink();
        }}
        storedReg={storedReg}
        // 2026-10-09: the hint follows the "Verify me" control (a saved registration WITH the
        // selected agency + the probe says the e-mail is unconfirmed) — no longer tied to the
        // in-memory submitted flag, which a page reload resets (the hint then never showed)
        showConfirmEmailHint={agencies.length > 0 && !!selectedAgency
          && !!storedReg && registeredWithSelectedAgency && statusLinksEnabled === false}
        // a confirmed e-mail hides the account actions (no confirmed chip since 2026-10-14)
        showEmailConfirmedHint={showConfirmedNotice}
        // on the confirmation views (WhatsApp code, e-mail, password link) the candidate is
        // already verifying — "Verify me" would only reopen the screen they are on; the done
        // screen (the end of "Confirm contact") carries its own e-mail status alert (2026-11-02)
        hideVerifyMe={phase === "verify" || phase === "done"}
        registeredWithSelectedAgency={registeredWithSelectedAgency}
        // T-SA-19.01: hidden on the landing — its Log in view leads to the same site
        showStatusLink={agencies.length > 0 && !!selectedAgency && (phase !== "apply" || onFunnel)}
        onForgetMyData={forgetMyData}
        // the agency name + "Temporary staffing" only — the provider-site address under the name
        // was removed (T-SA-21.04, PRD §21.4: it looked "like a broken link")
        agencyName={selectedAgency?.Name ?? ""}
        agencyAvailable={agencies.length > 0}
        agenciesLoaded={agenciesLoaded}
        onHome={goHome}
        onVerifyMe={() => {
          if (!storedReg) return;
          setCandidateId(storedReg.candidateId);
          setReg((r) => ({ ...r, whatsapp: storedReg.whatsapp, email: storedReg.email || r.email, chatbot: storedReg.chatbot }));
          setTelegramToken(storedReg.telegramToken || null);
          // THE E-MAIL FOCUS FIX (2026-10-29, the staging finding): when the probe says the
          // e-mail is NOT confirmed (the exact state that shows the "Click 'Verify me' to
          // confirm your e-mail" hint), the click MUST open the E-MAIL-focused verification
          // (the code entry + the password-setup link) — was statusIntent=false, which put the
          // phase in the registration-wizard mode where the WhatsApp-confirmed probe jumps
          // STRAIGHT to done: the e-mail verification was never reachable (the hint promised
          // what the click could not deliver).
          setStatusIntent(statusLinksEnabled === false);
          setPhase("verify");
          window.scrollTo({ top: 0 });
        }}
      />
      {/* The SHELL (T-SA-19.01 design, PRD §19.1): on the landing the LEFT side carries the message,
          the reference portal's sub-line and chips, the RIGHT panel the landing (Register | Log in).
          Once "Select jobs" opens the workflow (2026-10-21), the wizard steps, the contact confirmation
          and the done screen take the FULL width of the card — the left side is hidden. */}
      <main className={"shell" + (inWorkflow ? " shell-full" : "")}>
        <div className="shell-hero">
          <div>
            <h1>{config.heroTitle || t("landing.heroTitle")}</h1>
            <p className="shell-sub">{t("landing.heroSub")}</p>
          </div>
          {/* the reference portal's three chips (bottom of the left side) */}
          <ul className="shell-trust">
            {(["landing.trustFlexible", "landing.trustCareer", "landing.trustProfile"] as const).map((k) => (
              <li key={k}>
                <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true">
                  <path d="m5 12 5 5L19 7" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                {t(k)}
              </li>
            ))}
          </ul>
        </div>
        <div className="shell-panel">
          {phase === "done" ? (
            <>
              <StepBar current={FLOW_STEPS.length} />
              <Done
                agencyName={selectedAgency?.Name ?? config.agencyName}
                candidateName={reg.fullName}
                messenger={doneMessenger}
                onHome={goHome}
                statusLinksEnabled={statusLinksEnabled}
                onCheckStatus={() => {
                  void openStatusLink();
                }}
              />
            </>
          ) : phase === "verify" ? (
            <>
              {/* the check-status entry (e-mail focus) is not part of the registration progress */}
              {!statusIntent && <StepBar current={FLOW_STEPS.indexOf("verify")} />}
              <VerifyStep
                candidateId={candidateId}
                emailFocus={statusIntent}
                // the outcome of the Log-in-triggered resend (2026-10 fix): only the e-mail-focused
                // view consumes it, so a stale seed can never leak into the registration flow
                seedPasswordMail={statusIntent ? seedPasswordMail : null}
                portalUrl={portalUrl}
                onDoneIntent={() => setStatusIntent(false)}
                // the in-step CHANNEL SWITCH (2026-10-07): renders the OTHER channel's confirmation
                // card in place (both channels can end confirmed; the registered PRIMARY preference
                // stays untouched — changing the primary lives in the candidate cabinet)
                onSwitchChannel={(to) => {
                  setErr("");
                  setReg((r) => ({ ...r, chatbot: to }));
                  window.scrollTo({ top: 0 });
                }}
                whatsapp={reg.whatsapp}
                email={reg.email}
                chatbot={reg.chatbot}
                telegramToken={telegramToken}
                telegramBotUsername={telegramBotUsername}
                whatsappInboundNumber={whatsappInboundNumber}
                onTelegramTokenChanged={setTelegramToken}
                // 2026-10-09: the confirmed e-mail flips the header alert to the success state at
                // once (the probe effect only re-runs on an agency/registration/phase change)
                onEmailConfirmed={() => {
                  if (registeredWithSelectedAgency && storedReg?.candidateId === candidateId) setStatusLinksEnabled(true);
                }}
                onErr={setErr}
                err={err}
                // 2026-10-08: the DONE step's reach-out wording AND the toast name the messenger the
                // candidate actually confirmed: "telegram" (already toasted by the linking flow),
                // "whatsapp" (the code confirmed), "none" (the candidate skipped both) — the neutral
                // wording, no toast.
                onWhatsAppVerified={(m) => {
                  setDoneMessenger(m === "telegram" ? "telegram" : m === "none" ? null : "whatsapp");
                  setPhase("done");
                  if (m !== "telegram" && m !== "none") toast(t("toast.whatsappConfirmed"));
                  window.scrollTo({ top: 0 });
                }}
              />
            </>
          ) : phase === "apply" && !onFunnel ? (
            <Landing
              agencies={agencies}
              agenciesLoaded={agenciesLoaded}
              selectedAgency={selectedAgency}
              onPickAgency={pickAgency}
              onRegister={start}
              // 2026-10-22: "Already registered?" leaves for the agency portal in THIS tab
              onLogin={() => window.location.assign(portalUrl)}
              // the password-link step needs THIS browser's confirmed registration (T-SA-18.14)
              onPasswordLink={showConfirmedNotice ? openPasswordLink : undefined}
            passwordLinkBusy={passwordLinkBusy}
            />
          ) : (
            <section id="funnel" className="funnel">
              {/* The step bar with Cancel on its right (2026-10-02 Cancel, §13 row 43: abandons
                  the application → the landing, where the agency selection is changeable again);
                  the finished steps are clickable before the submit. */}
              <StepBar current={stepIdx} onCancel={cancel} onJump={jumpToStep} />
              <div id="fbody">
                {wizardError ? (
                  <div className="dicterror">
                    <div className="fh1">{t("wizard.errorTitle")}</div>
                    <div className="fsub">{wizardError}</div>
                    <button className="btn" type="button" onClick={() => setWizardTick((n) => n + 1)}>
                      {t("err.retry")}
                    </button>
                  </div>
                ) : !wizard ? (
                  <div className="fsub">{t("wizard.loading")}</div>
                ) : (
                  <>
                    {stepIdx === 0 && (
                      <JobsStep
                        categories={wizard.categories}
                        jobTypes={wizard.jobTypes}
                        next={next}
                        back={back}
                        {...stepProps}
                      />
                    )}
                    {stepIdx === 1 && (
                      <RegionsStep
                        regions={wizard.regions}
                        operatesInCountryCode={countryOptions[0]?.code ?? wizard.activityCountry?.code ?? ""}
                        next={next}
                        back={back}
                        {...stepProps}
                      />
                    )}
                    {stepIdx === 2 && <TermsStep next={next} back={back} {...stepProps} />}
                    {stepIdx === 3 && (
                      <AboutStep
                        questions={visibleQuestions}
                        docTypes={docTypes}
                        agencyName={selectedAgency?.Name ?? config.agencyName}
                        back={back}
                        submit={submit}
                        {...stepProps}
                      />
                    )}
                  </>
                )}
              </div>
            </section>
          )}
        </div>
      </main>
      <footer className="sitefoot">
        <TermsLink>{t("landing.termsFooter")}</TermsLink>
      </footer>
      <div id="toast" role="status" />
    </>
  );
}

function Header({
  storedReg,
  registeredWithSelectedAgency,
  showStatusLink,
  showConfirmEmailHint,
  showEmailConfirmedHint,
  onForgetMyData,
  onVerifyMe,
  hideVerifyMe,
  onCheckStatus,
  agencyName,
  agencyAvailable,
  agenciesLoaded,
  onHome,
}: {

  storedReg: StoredRegistration | null;
  /** The selected agency = the one the candidate registered with (the Verify-me rule). */
  registeredWithSelectedAgency: boolean;
  /** 2026-10-08: "Already registered?" renders only with a SELECTED agency (computed in App). */
  showStatusLink: boolean;
  /** 2026-10-08: "Confirm your email to log in" hint — a selected agency + a saved registration + submitted + unconfirmed (computed in App). */
  showConfirmEmailHint: boolean;
  /** 2026-10-09: the confirmed e-mail — hides the account actions; nothing replaces them in the
   *  header (the confirmed chip was removed 2026-10-14). */
  showEmailConfirmedHint: boolean;
  /** 2026-10-08: "Forget my data" — clears the per-agency saved registration (localStorage). */
  onForgetMyData: () => void;
  onVerifyMe: () => void;
  /** T-SA-19.01: on the verify step (the WhatsApp / e-mail / password-link views) "Verify me"
   *  hides — in the account pill AND in the confirm-e-mail banner; the view carries the
   *  verification itself. */
  hideVerifyMe: boolean;
  /** The gated status link (2026-10-08): ALWAYS clickable; the probe/fire-and-forget only informs the hints. */
  onCheckStatus?: () => void;
  agencyName: string;
  /** false when the server API is unreachable or returns no agencies — the top bar left
   * then shows "No agency available" and the status link is hidden (PRD §5.1, 2026-10-02). */
  agencyAvailable: boolean;
  agenciesLoaded: boolean;
  /** HOME: back to the landing from any phase (2026-10-02). */
  onHome: () => void;
}) {
  // the confirm-e-mail banner ("Click 'Verify me'…") hides on the verify step — the e-mail card is
  // right there; its "Forget my data" then returns to the account actions (T-SA-19.01)
  const confirmBannerShown = showConfirmEmailHint && !hideVerifyMe;
  // phones (≤640px): ONLY the top row (home · agency · language) stays pinned; the status link and
  // the account actions move to a row UNDER the header that scrolls away with the page
  const compact = useCompactHeader();
  const top = (
    <>
        <button className="homebtn" type="button" onClick={onHome} title={t("header.home")} aria-label={t("header.home")}>
          {/* the Safeguard Global jobs logo (2026-10-15) — the aria-label keeps the accessible name */}
          <img className="homelogo" src={homeLogo} alt="" />
        </button>
        <div className="brand">
          {!agenciesLoaded ? null : !agencyAvailable ? (
            // server down / 502 / empty list — nothing can be advertised (2026-10-02)
            <span className="noagency">{t("header.noAgency")}</span>
          ) : agencyName ? (
            // no agency-initial badge (2026-10-14) — the HOME logo carries the brand
            <>
              <span>
                <b>{agencyName}</b>
                <small>{t("header.staffing")}</small>
              </span>
            </>
          ) : null}
        </div>
        <span className="sp" />
    </>
  );
  const links = (
        <span className="hlinks">
          {/* The status link (the selected agency's InternalProviderSite — hidden while no agency
              can be advertised; 2026-10-02) — GATED: enabled only when the SELECTED agency's
              registration has its e-mail CONFIRMED (the async probe); otherwise DISABLED. */}
          {showStatusLink && (
            <button
              type="button"
              className="statusbtn"
              /* 2026-10-08: ALWAYS clickable once an agency is selected — the click redirects
                 to the agency portal (the candidate login); the unconfirmed-e-mail warning
                 hints render below (the probe informs them), but never block the link. */
              title={t("header.loginTooltip")}
              onClick={onCheckStatus}
            >
              {compact ? (
                <>
                  <span className="sb-q">{t("header.alreadyRegisteredQ")}</span>{" "}
                  <span className="sb-a">{t("header.checkMyStatus")}</span>
                </>
              ) : (
                t("header.alreadyRegistered")
              )}
            </button>
          )}
          {/* The account actions (far right — the identity convention): ONLY for the
              REGISTERED-WITH-THE-SELECTED-AGENCY candidate (2026-10-08) — "Verify me" + the
              inline-confirmed "Forget my data" (per-agency localStorage). While the confirm-e-mail
              alert shows, BOTH move INTO the alert (2026-10-09) — never shown twice; once the e-mail is
              confirmed they hide (2026-10-14). */}
          {storedReg && registeredWithSelectedAgency && !confirmBannerShown && !showEmailConfirmedHint && (
            <AccountActions
              fullName={storedReg.fullName}
              agencyName={agencyName}
              // T-SA-19.01: on the confirmed-e-mail view the verify action lives IN the view
              onVerifyMe={hideVerifyMe ? undefined : onVerifyMe}
              onForgetMyData={onForgetMyData}
            />
          )}
        </span>
  );
  // 2026-10-08: the "Confirm your e-mail to log in." banner — full width under the header:
  // the warning + the click-'Verify me' hint + the account actions (Verify me / Forget)
  const banner = confirmBannerShown && (
        <div className="wrap hbanner">
          <VerifyHint
            title={t("header.confirmEmailToLogin")}
            verifyLabel={storedReg ? t("header.verifyMeWithName", { name: verifyMeName(storedReg.fullName) }) : undefined}
            onVerifyMe={storedReg && registeredWithSelectedAgency ? onVerifyMe : undefined}
            onForgetMyData={storedReg && registeredWithSelectedAgency ? onForgetMyData : undefined}
          />
        </div>
  );
  if (compact) {
    return (
      <>
        <header className="app-h compact">
          <div className="wrap hrow">
            {top}
            <LanguageSelect />
          </div>
        </header>
        <div className="hsub">
          <div className="wrap">{links}</div>
          {banner}
        </div>
      </>
    );
  }
  return (
    <header className="app-h" style={{padding : "16px 10px"}}>
      <div className="wrap hrow" style ={{height : "unset", minHeight : "64px"}}>
        {top}
        {/* the language buttons sit in the TOP row (outside the links group): on phones the links
            group wraps to its own row, the languages stay top-right */}
        <LanguagePicker />
        {links}
      </div>
      {banner}
    </header>
  );
}

/** Phones (≤640px): the compact header (option A, 2026-10-14) — follows the viewport live.
 *  No matchMedia (jsdom) = the regular header. */
function useCompactHeader(): boolean {
  const query = "(max-width:640px)";
  const [compact, setCompact] = useState(() => !!window.matchMedia?.(query).matches);
  useEffect(() => {
    const mq = window.matchMedia?.(query);
    if (!mq) return;
    const onChange = () => setCompact(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return compact;
}

/** The compact header's language switcher: a small native select (EN ▾) — the phone's own
 *  picker opens; same store as the LanguagePicker buttons. */
function LanguageSelect() {
  const locale = useLocale();
  return (
    <select className="langselect" aria-label="Language" value={locale} onChange={(e) => setLocale(e.target.value as Locale)}>
      {SUPPORTED_LOCALES.map((l) => (
        <option key={l} value={l} title={LANGUAGE_NAMES[l]}>
          {LANG_SHORT[l]}
        </option>
      ))}
    </select>
  );
}

/**
 * The header account actions (2026-10-09): visible at the far right (no popover) — the
 * "Verify me" identity pill (initials + short name + shield) and, beside it, the red
 * "Forget my data" pill. Hidden while the confirm-e-mail alert carries both actions.
 */
function AccountActions({
  fullName,
  agencyName,
  onVerifyMe,
  onForgetMyData,
}: {
  fullName: string;
  agencyName: string;
  /** undefined = no "Verify me" pill (the confirmed-e-mail view carries its own verify action). */
  onVerifyMe?: () => void;
  onForgetMyData: () => void;
}) {
  return (
    <div className="acct" role="group" aria-label={t("header.accountMenu")}>
      {onVerifyMe && (
        <button
          type="button"
          className="acctbtn"
          onClick={onVerifyMe}
          title={agencyName ? t("header.registeredWith", { agency: agencyName }) : undefined}
        >
          <span className="acctav" aria-hidden="true">{initials(fullName)}</span>
          <span className="acctname">
            {t("header.verifyMeWithName", { name: verifyMeName(fullName) })}
          </span>
          <svg className="accticon" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
            <path d="M12 2 4 5v6c0 5 3.4 9.4 8 11 4.6-1.6 8-6 8-11V5l-8-3Z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
            <path d="m8.5 12 2.5 2.5 4.5-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}
      <ForgetMyDataButton onForgetMyData={onForgetMyData} />
    </div>
  );
}

/**
 * "Forget my data" (2026-10-09): the red pill that swaps IN PLACE to an inline confirmation
 * (Cancel / Forget) before clearing — nothing is removed on a single accidental click.
 * Used in the header account actions and inside the confirm-e-mail alert.
 */
function ForgetMyDataButton({ onForgetMyData }: { onForgetMyData: () => void }) {
  const [confirming, setConfirming] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const forgetRef = useRef<HTMLButtonElement>(null);

  // focus follows the swap: Cancel (the safe default) on entry, the Forget pill on cancel
  useEffect(() => {
    if (confirming) cancelRef.current?.focus();
  }, [confirming]);

  const cancel = () => {
    setConfirming(false);
    requestAnimationFrame(() => forgetRef.current?.focus());
  };

  return !confirming ? (
    <button ref={forgetRef} type="button" className="forgebtn" onClick={() => setConfirming(true)}>
      <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
        <path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {t("header.forgetMyData")}
    </button>
  ) : (
    <div
      className="forgeconfirm"
      role="alertdialog"
      aria-labelledby="forge-title"
      aria-describedby="forge-body"
      onKeyDown={(e) => {
        if (e.key === "Escape") cancel();
      }}
    >
      <span id="forge-title" className="forgeq" title={t("header.forgetConfirmBody")}>
        {t("header.forgetConfirmTitle")}
      </span>
      <span id="forge-body" hidden>{t("header.forgetConfirmBody")}</span>
      <button ref={cancelRef} type="button" className="forgeghost" onClick={cancel}>
        {t("header.cancel")}
      </button>
      <button
        type="button"
        className="forgedanger"
        onClick={() => {
          setConfirming(false);
          onForgetMyData();
        }}
      >
        {t("header.forgetConfirm")}
      </button>
    </div>
  );
}

const LANG_SHORT: Record<Locale, string> = { en: "EN", es: "ES", fr: "FR" };

/**
 * Language switcher (PRD §16.1, 2026-10-02): the site follows the browser preference by
 * default (English fallback); the user's choice is stored in localStorage (`staffing.language`)
 * and takes precedence on the next loads. The selected language is the highlighted one.
 * When the browser preference differs from the displayed one — and the browser preference is
 * one of the supported languages — a tip written IN THE BROWSER'S LANGUAGE invites the user
 * to use the switcher (never shown when the browser prefers an unsupported language).
 */
function LanguagePicker() {
  const locale = useLocale();
  const browser = browserLocaleBase();
  // tip rule: the displayed language differs from the browser preference — and the browser
  // preference is a supported locale (browser !== null implies it). With no stored choice the
  // displayed language IS the browser's, so the tip can only appear after a manual pick.
  const tip = browser !== null && browser !== locale && hasStoredLocale();
  return (
    <span className="langbox">
      {tip && (
        <span className="langnote" title={LANGUAGE_NAMES[browser]}>
          {tFor(browser, "lang.tip", { language: LANGUAGE_NAMES[browser] })}
        </span>
      )}
      <span className="langsel" role="group" aria-label="Language">
        {SUPPORTED_LOCALES.map((l) => (
          <button
            key={l}
            type="button"
            className={"langbtn" + (l === locale ? " on" : "")}
            title={LANGUAGE_NAMES[l]}
            aria-pressed={l === locale}
            onClick={() => setLocale(l)}
          >
            {LANG_SHORT[l]}
          </button>
        ))}
      </span>
    </span>
  );
}

/**
 * The landing's RIGHT panel (T-SA-19.01, PRD §19.1; T-SA-23.05 design): the visitor can only
 * REGISTER or LOG IN.
 * - the big yellow "Register here" opens the full-width wizard at the jobs step (the terms checkbox
 *   is the wizard's terms step).
 * - under it, "Already registered? Log in here" opens the selected agency's InternalProviderSite in
 *   the SAME tab.
 */
function Landing({
  agencies,
  agenciesLoaded,
  selectedAgency,
  onPickAgency,
  onRegister,
  onLogin,
  passwordLinkBusy,
  onPasswordLink,
}: {
  agencies: StaffingAgencyDTO[];
  /** false while the agencies lookup is in flight (avoids a "no agency" flash). */
  agenciesLoaded: boolean;
  selectedAgency: StaffingAgencyDTO | null;
  onPickAgency: (a: StaffingAgencyDTO) => void;
  /** "Register here": opens the wizard at the jobs step. */
  onRegister: () => void;
  /** Navigates to the selected agency's InternalProviderSite (the "Check my status" target). */
  onLogin: () => void;
  /** The password-link step (T-SA-18.14) — only with a confirmed registration on this browser;
   *  undefined = no password help. */
  onPasswordLink?: () => void | Promise<void>;
  /** 2026-10 fix: the resend is in flight — the button is busy (single pending request). */
  passwordLinkBusy?: boolean;
}) {
  return (
    <section id="landing">
      {agencies.length === 0 ? (
        // server unreachable (down / 502) or genuinely no agencies for this site
        // (PRD §5.1, 2026-10-02): nothing selectable — say so instead of the buttons.
        agenciesLoaded && <div className="lempty">{t("landing.noAgency")}</div>
      ) : (
        <>
          {/* SINGLE-SELECT (§13 row 43): only shown when there is a choice — the single agency
              of a deployment is auto-selected (2026-10-02). */}
          {agencies.length > 1 && (
            <div className="lagencies" role="group" aria-labelledby="lagencies-lbl">
              <span className="lagencylbl" id="lagencies-lbl">{t("landing.applyingTo")}<Req /></span>
              {agencies.map((a) => (
                <button
                  key={a.Id || a.Name}
                  type="button"
                  className={"lagency" + (selectedAgency?.Id === a.Id ? " on" : "")}
                  onClick={() => onPickAgency(a)}
                  aria-pressed={selectedAgency?.Id === a.Id}
                >
                  <span className="lradio" aria-hidden="true" />
                  <span>
                    <b>{a.Name}</b>
                    {a.ActivityCountry?.Code && (
                      <small>{countryLabel(a.ActivityCountry.Code, a.ActivityCountry.Name)}</small>
                    )}
                  </span>
                </button>
              ))}
            </div>
          )}
          {/* the selected agency's sales copy (contract PublicDescription) — the agency's own text,
              not translated (PRD §11.2); nothing renders without one */}
          {selectedAgency?.PublicDescription?.trim() && (
            // keyed by the agency: a new pick remounts the card (collapsed, fade-in)
            <AgencyAbout
              key={selectedAgency.Id || selectedAgency.Name}
              agencyName={selectedAgency.Name}
              description={selectedAgency.PublicDescription.trim()}
            />
          )}
          <div className="lchoices">
            <button type="button" className="lcta" onClick={onRegister} disabled={!selectedAgency}>
              {t("landing.register")}
              {/* no agency picked yet: the reason the button is disabled, under its label */}
              {!selectedAgency && <small className="lctahint">{t("landing.selectAgencyFirst")}</small>}
            </button>
            <button type="button" className="toplink llogin" onClick={onLogin} disabled={!selectedAgency}>
              {t("landing.alreadyRegistered")}
            </button>
          </div>
          {/* The password link (T-SA-18.14): logging in needs the password the candidate sets from
              the e-mailed link; this explains that and opens the resend step. Only with a confirmed
              registration here. */}
          {selectedAgency && onPasswordLink && (
            <div className="lpassword">
              <b>{t("landing.passwordHelpTitle")}</b>
              <p className="fsub">{t("landing.passwordHelpBody")}</p>
              <button type="button" className="hcalloutbtn" disabled={passwordLinkBusy} onClick={onPasswordLink}>
                {t("email.password.resend")}
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}

/**
 * The selected agency's description card on the landing (above "Register here"): an eyebrow with
 * the agency name + its sales copy, clamped to a few lines — "Read more" shows only when the text
 * actually overflows the clamp (measured, re-measured on resize).
 */
function AgencyAbout({ agencyName, description }: { agencyName: string; description: string }) {
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    const el = textRef.current;
    if (!el) return;
    const measure = () => {
      // only meaningful while clamped; once expanded the toggle stays to collapse again
      if (!el.classList.contains("open")) setOverflows(el.scrollHeight > el.clientHeight + 1);
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [description]);

  return (
    <section className="labout" aria-labelledby="labout-title">
      <div className="labout-head">
        <span id="labout-title" className="labout-title">{agencyName}</span>
      </div>
      <p ref={textRef} id="labout-text" className={"labout-text" + (expanded ? " open" : "")}>
        {description}
      </p>
      {(overflows || expanded) && (
        <button
          type="button"
          className="labout-more"
          aria-expanded={expanded}
          aria-controls="labout-text"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? t("landing.showLess") : t("landing.readMore")}
          <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
            <path d="m6 9 6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}
    </section>
  );
}

/**
 * The registration STEP BAR (T-SA-19.01 design, 2026-10-13): the LEFT side of the page never
 * changes, so the progress lives at the top of the RIGHT panel — "Step 2 of 5 · Regions" + a
 * one-row bar per step (finished = dark, current = yellow). Before the submit the finished steps are
 * clickable (onJump); Cancel sits on the right while the wizard is open (onCancel).
 * `current` = the FLOW_STEPS index; FLOW_STEPS.length = all steps completed (the done screen).
 */
function StepBar({
  current,
  onJump,
  onCancel,
}: {
  current: number;
  onJump?: (flowIdx: number) => void;
  onCancel?: () => void;
}) {
  const allDone = current >= FLOW_STEPS.length;
  // mobile (the stacked shell, ≤850px): the hero sits ABOVE the panel, so the handlers'
  // scrollTo(0) would show the hero — on every step change bring "Step n of N" to the top,
  // right under the sticky header (this effect runs after the handlers' scrollTo)
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !window.matchMedia?.("(max-width:850px)").matches) return;
    const headerH = document.querySelector(".app-h")?.getBoundingClientRect().height ?? 0;
    window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - headerH - 8 });
  }, [current]);
  return (
    <div className="stepbar" ref={ref}>
      <div className="stepbar-top">
        <span className="stepbar-caption">
          {allDone
            ? t("steps.allDone")
            : t("steps.caption", {
                n: String(current + 1),
                total: String(FLOW_STEPS.length),
                label: t(FLOW_STEP_LABELS[FLOW_STEPS[current]]),
              })}
        </span>
        {onCancel && (
          <button className="stepbar-cancel" type="button" onClick={onCancel}>
            <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
              <path d="M6 6l12 12M18 6 6 18" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
            </svg>
            {t("wizard.cancel")}
          </button>
        )}
      </div>
      <ol className="stepbar-track" aria-label={t("steps.progress")}>
        {FLOW_STEPS.map((s, i) => {
          const done = i < current;
          const cur = i === current;
          const label = t(FLOW_STEP_LABELS[s]);
          const inner = (
            <>
              <span className="stepbar-bar" />
              <span className="stepbar-label">{label}</span>
            </>
          );
          return (
            <li key={s} className={done ? "done" : cur ? "cur" : ""} aria-current={cur ? "step" : undefined}>
              {done && onJump ? (
                <button type="button" onClick={() => onJump(i)} title={t("steps.backTo", { label })}>
                  {inner}
                </button>
              ) : (
                <span>{inner}</span>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/** The required-field marker: a red asterisk after a mandatory field's label (visual only —
 *  screen readers get the field without it; the submit validation names what is missing). */
function Req() {
  return <span className="req" aria-hidden="true"> *</span>;
}

/**
 * A legal-document link: the deployment's URL in a new tab; until the document exists the link is
 * a placeholder that only says so (toast) — PRD §17 row 18.
 */
function DocLink({ url, pending, children }: { url: string; pending: TKey; children: ReactNode }) {
  return url ? (
    <a href={url} target="_blank" rel="noreferrer">
      {children}
    </a>
  ) : (
    <a
      href="#terms"
      onClick={(e) => {
        e.preventDefault();
        toast(t(pending));
      }}
    >
      {children}
    </a>
  );
}

/** The terms of use (VITE_TERMS_OF_USE_URL). */
function TermsLink({ children }: { children: ReactNode }) {
  return <DocLink url={config.termsOfUseUrl} pending="landing.termsPending">{children}</DocLink>;
}

/** The privacy policy (VITE_PRIVACY_POLICY_URL). */
function PrivacyLink({ children }: { children: ReactNode }) {
  return <DocLink url={config.privacyPolicyUrl} pending="landing.privacyPending">{children}</DocLink>;
}

interface StepProps {
  reg: Registration;
  setReg: Dispatch<SetStateAction<Registration>>;
  err: string;
  /** 2026-10-08: the registration submit in-flight (the Submit button spinner). */
  submitting: boolean;
  /** Chatbot PRD §3.4 (2026-10-03): a configured bot (server handle or env fallback) is
   *  required for the Telegram choice — off = the channel renders only WhatsApp. */
  telegramAvailable: boolean;
}

/** The agency-country code of a given agency record ("" when missing → regions fallback). */
function agencyCountryCodeFor(agency: StaffingAgencyDTO | null): string {
  return (agency?.ActivityCountry?.Code ?? "").trim();
}

/** Document-answer questions (AnswerType 1; falls back to the legacy RequiresDocumentUpload flag). */
function isDocumentQuestion(q: StaffingScreeningQuestionDTO): boolean {
  return (q.AnswerType ?? (q.RequiresDocumentUpload ? 1 : 0)) === 1;
}

/** The youngest candidate the registration accepts (the legal minimum working age in ES/FR/CH).
 *  Keep in step with the "submit.err.dobAge" texts and the server's RegisterStaffingCandidateCommandValidator. */
const MIN_CANDIDATE_AGE = 16;
/** Older than this is treated as a typo (e.g. 1906 for 1996). */
const MAX_CANDIDATE_AGE = 100;

/** Today minus `years` as the local "YYYY-MM-DD" — the date input's min/max and the DOB range check
 *  (ISO dates compare correctly as strings). */
function isoDateYearsAgo(years: number): string {
  const d = new Date();
  d.setFullYear(d.getFullYear() - years);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** The About step's mandatory-field check — the FIRST missing/invalid field's error key, or null
 *  when the form is complete. Shared by the submit (the error message) and the Submit button
 *  (disabled while anything mandatory is missing). */
function aboutFormError(reg: Registration, questions: StaffingScreeningQuestionDTO[]): TKey | null {
  const name = reg.fullName.trim();
  if (name.length < 3) return "submit.err.name";
  if (!reg.birthDate) return "submit.err.dob";
  // Bug #2 (QA 2026-10-21): today / future dates and under-age candidates were accepted
  if (reg.birthDate > isoDateYearsAgo(MIN_CANDIDATE_AGE)) return "submit.err.dobAge";
  if (reg.birthDate < isoDateYearsAgo(MAX_CANDIDATE_AGE)) return "submit.err.dobInvalid";
  if (reg.gender === "") return "submit.err.gender";
  if (!reg.taxId.trim()) return "submit.err.taxId";
  if (reg.chatbot === "whatsapp") {
    if (reg.whatsapp.replace(/\D/g, "").length < 9) return "submit.err.whatsapp";
    if (!reg.whatsappOptIn) return "submit.err.consent";
  }
  if (!/^\S+@\S+\.\S+$/.test(reg.email)) return "submit.err.email";
  if (name.split(/\s+/).length < 2) return "submit.err.flName";
  const unanswered = questions.some((q) =>
    isDocumentQuestion(q) ? !reg.files[q.Id] : !(reg.answers[q.Id] ?? "").trim()
  );
  if (unanswered) return "submit.err.questions";
  return null;
}

/** The wizard's FIRST step (2026-10-21: in the full-width workflow, opened by "Select jobs"). */
function JobsStep({
  categories,
  jobTypes,
  next,
  back,
  reg,
  setReg,
  err,
}: StepProps & {
  categories: WorkCategoryDTO[];
  jobTypes: JobTypeDTO[];
  next: () => void;
  back: () => void;
}) {
  const jobsCount = reg.wantedJobs.length;
  return (
    <>
      <div className="fh1">{t("jobs.title")}<Req /></div>
      <div className="fsub">{t("jobs.subtitle")}</div>
      <JobsPicker categories={categories} jobTypes={jobTypes} reg={reg} setReg={setReg} />
      <div className="err">{err}</div>
      <div className="fnav">
        <button className="btn ghost" onClick={back}>
          {t("wizard.back")}
        </button>
        <button className="btn" onClick={next} disabled={jobsCount === 0}>
          {t("wizard.continue")}
          {jobsCount > 0 && ` · ${t("landing.jobsSelected", { count: String(jobsCount) })}`}
        </button>
      </div>
    </>
  );
}

/** The job-type chips grouped by work category — the wizard's FIRST step. */
function JobsPicker({
  categories,
  jobTypes,
  reg,
  setReg,
}: {
  categories: WorkCategoryDTO[];
  jobTypes: JobTypeDTO[];
  reg: Registration;
  setReg: Dispatch<SetStateAction<Registration>>;
}) {
  const toggleJob = (id: string) =>
    setReg((r) => {
      const wantedJobs = r.wantedJobs.includes(id)
        ? r.wantedJobs.filter((j) => j !== id)
        : [...r.wantedJobs, id];
      return { ...r, wantedJobs };
    });
  // Only the categories that hold one of the SELECTED AGENCY's job types (PRD §5.1/§16.7,
  // 2026-10-02) — the job types themselves are already agency-scoped server-side.
  // Kept in the order the wizard endpoint sends: the agency's own category order first, then the
  // default SortOrder, then name (server StaffingAgencyWorkCategoryOrder.Sort) — do not re-sort here.
  const visibleCategories = categories.filter((c) => jobTypes.some((j) => j.WorkCategoryId === c.Id));
  return (
    <>
      {visibleCategories.length === 0 && <div className="fsub">{t("jobs.empty")}</div>}
      {visibleCategories.map((c) => (
        <div key={c.Id}>
          <div className="gh">{c.Name}</div>
          <div className="chips mar">
            {jobTypes
              .filter((j) => j.WorkCategoryId === c.Id)
              .map((j) => (
                <button
                  key={j.Id}
                  type="button"
                  className={"chip" + (reg.wantedJobs.includes(j.Id) ? " on" : "")}
                  aria-pressed={reg.wantedJobs.includes(j.Id)}
                  onClick={() => toggleJob(j.Id)}
                >
                  {j.Name}
                </button>
              ))}
          </div>
        </div>
      ))}
    </>
  );
}

function RegionsStep({
  regions,
  next,
  back,
  reg,
  setReg,
  err,
  operatesInCountryCode,
}: StepProps & {
  regions: RegionDTO[];
  next: () => void;
  back: () => void;
  /** The agency's activity country (ISO code). */
  operatesInCountryCode?: string | null;
}) {
  // the agency's allowed regions may span several countries (2026-10-21): the chips name their
  // country only when they are not all the agency's own
  const singleCountry =
    !!operatesInCountryCode &&
    regions.every((r) => (r.CountryCode ?? "").toUpperCase() === operatesInCountryCode.toUpperCase());
  const toggleRegion = (id: string) =>
    setReg((s) => ({
      ...s,
      regions: s.regions.includes(id) ? s.regions.filter((x) => x !== id) : [...s.regions, id],
    }));
  return (
    <>
      {/* 2026-10-23: the heading names no country — "Which regions can you work in?" */}
      <div className="fh1">
        {t("regions.title")}
        <Req />
      </div>
      <div className="fsub">{t("regions.subtitle")}</div>
      <div className="chips mar">
        {regions.map((r) => (
          <button
            key={r.Id}
            type="button"
            className={"chip" + (reg.regions.includes(r.Id) ? " on" : "")}
            onClick={() => toggleRegion(r.Id)}
          >
            {r.Name}
            {!singleCountry && r.CountryCode ? ` · ${countryLabel(r.CountryCode)}` : ""}
          </button>
        ))}
      </div>
      <div className="err">{err}</div>
      <div className="fnav">
        <button className="btn ghost" onClick={back}>
          {t("wizard.back")}
        </button>
        <button className="btn" onClick={next} disabled={reg.regions.length === 0}>
          {t("wizard.continue")}
        </button>
      </div>
    </>
  );
}

/** The wizard's TERMS step (T-SA-23.05): after jobs and regions, before the personal data —
 *  moved here from the landing; Continue stays disabled until the box is ticked. */
function TermsStep({
  next,
  back,
  reg,
  setReg,
  err,
}: StepProps & {
  next: () => void;
  back: () => void;
}) {
  return (
    <>
      <div className="fh1">{t("terms.title")}</div>
      <div className="fsub">{t("terms.subtitle")}</div>
      <label className="lcheck">
        <input type="checkbox" checked={reg.termsAgreed} onChange={(e) => setReg((r) => ({ ...r, termsAgreed: e.target.checked }))} />
        <span>
          {t("landing.agreePrefix")} <TermsLink>{t("landing.termsLink")}</TermsLink>{" "}
          {t("landing.agreeAnd")} <PrivacyLink>{t("landing.privacyLink")}</PrivacyLink>
          <Req />
        </span>
      </label>
      <div className="err">{err}</div>
      <div className="fnav">
        <button className="btn ghost" onClick={back}>
          {t("wizard.back")}
        </button>
        <button className="btn" onClick={next} disabled={!reg.termsAgreed}>
          {t("wizard.continue")}
        </button>
      </div>
    </>
  );
}

function AboutStep({
  questions,
  docTypes,
  agencyName,
  back,
  submit,
  reg,
  setReg,
  err,
  telegramAvailable,
  submitting,
}: StepProps & {
  /** Pre-scoped by the candidate's wanted jobs ("shown to" rules of PRD §4.11). */
  questions: StaffingScreeningQuestionDTO[];
  docTypes: RegistrationDocumentTypeDTO[];
  agencyName: string;
  /** Chatbot PRD §7 + §3.4 (2026-10-03 ruling): the Telegram choice is offered ONLY when
   *  the deployment carries a configured bot — server-provided handle or the env fallback. */
  telegramAvailable: boolean;
  back: () => void;
  submit: () => void;
}) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <div className="fh1">{t("about.title")}</div>
      <div className="fsub">{t("about.subtitle")}</div>
      <div className="row">
        <div className="fld">
          <label htmlFor="a_name">{t("about.fullName")}<Req /></label>
          <input className="in" id="a_name" value={reg.fullName} onChange={(e) => setReg((r) => ({ ...r, fullName: e.target.value }))} />
        </div>
        <div className="fld">
          <label htmlFor="a_dob">{t("about.birthDate")}<Req /></label>
          {/* min/max also open the picker on an adult year instead of the current one */}
          <input
            className="in"
            id="a_dob"
            type="date"
            min={isoDateYearsAgo(MAX_CANDIDATE_AGE)}
            max={isoDateYearsAgo(MIN_CANDIDATE_AGE)}
            value={reg.birthDate}
            onChange={(e) => setReg((r) => ({ ...r, birthDate: e.target.value }))}
          />
        </div>
      </div>
      {/* PRD §24.7: the gender, mandatory (Male / Female / I prefer not to disclose) */}
      <div className="row">
        <div className="fld">
          <label htmlFor="a_gender">{t("about.gender")}<Req /></label>
          <select className="in" id="a_gender" value={reg.gender} onChange={(e) => setReg((r) => ({ ...r, gender: e.target.value as Registration["gender"] }))}>
            <option value="" disabled>{t("about.gender.pick")}</option>
            <option value="0">{t("about.gender.male")}</option>
            <option value="1">{t("about.gender.female")}</option>
            <option value="2">{t("about.gender.preferNotToDisclose")}</option>
          </select>
        </div>
        <div className="fld">
          <label htmlFor="a_nid">{t("about.taxId")}<Req /></label>
          <input className="in" id="a_nid" value={reg.taxId} onChange={(e) => setReg((r) => ({ ...r, taxId: e.target.value }))} />
        </div>
      </div>
      {/* the contact choice with the WhatsApp number beside it (the number only for WhatsApp) */}
      <div className="row" style={{ marginBottom: 16 }}>
        {/* Chatbot PRD §7: the candidate PICKS the primary chatbot - WhatsApp (number + consent)
            or Telegram (no number; the chat is linked on the next step via /start and the
            phone share). 2026-10-10: the PICKER is a checkbox pair (real checkboxes — the
            label + the whole card click) with a CLEARLY HIGHLIGHTED selection (the purple fill
            + the matching border on the checked card, the muted ghost on the unchecked one). */}
        <div className="fld">
          <label htmlFor="a_chatbot">{t("about.chatbot")}</label>
          <div className="chatcheck">
            <label
              className={`chatcheckitem ${reg.chatbot === "whatsapp" || !telegramAvailable ? "on" : ""}`}
              htmlFor="a_chatbot_whatsapp"
            >
              <input
                id="a_chatbot_whatsapp"
                type="checkbox"
                checked={reg.chatbot === "whatsapp" || !telegramAvailable}
                onChange={() => setReg((r) => ({ ...r, chatbot: "whatsapp" }))}
              />
              <span className="chatcheckname">{t("about.chatbotWhatsapp")}</span>
              <span className="chatchecktick" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="14" height="14">
                  <path d="m5 12 5 5L19 7" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
            </label>
            {/* hidden when the server reports no configured bot (chatbot PRD §3.4) */}
            {telegramAvailable && (
              <label
                className={`chatcheckitem ${reg.chatbot === "telegram" ? "on" : ""}`}
                htmlFor="a_chatbot_telegram"
              >
                <input
                  id="a_chatbot_telegram"
                  type="checkbox"
                  checked={reg.chatbot === "telegram"}
                  onChange={() => setReg((r) => ({ ...r, chatbot: "telegram" }))}
                />
                <span className="chatcheckname">{t("about.chatbotTelegram")}</span>
                <span className="chatchecktick" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="14" height="14">
                    <path d="m5 12 5 5L19 7" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              </label>
            )}
          </div>
        </div>
        {reg.chatbot === "whatsapp" && (
          <div className="fld">
            <label htmlFor="a_wa">{t("about.whatsapp")}<Req /></label>
            <input
              className="in"
              id="a_wa"
              type="tel"
              placeholder="+34 600 000 000"
              value={reg.whatsapp}
              onChange={(e) => setReg((r) => ({ ...r, whatsapp: e.target.value }))}
            />
          </div>
        )}
      </div>
      {reg.chatbot === "whatsapp" ? (
        <>
          <label className="consent">
            <input type="checkbox" checked={reg.whatsappOptIn} onChange={(e) => setReg((r) => ({ ...r, whatsappOptIn: e.target.checked }))} />
            <span>{t("about.consent")}<Req /></span>
          </label>
        </>
      ) : (
        <>
          <div className="fsub">{t("about.telegramNote")}</div>
          {/* the SAME consent declaration for the Telegram-primary candidates (2026-10-07):
              on Telegram the /start binding stays the technical consent proof — the checkbox
              records the candidate's explicit declaration at registration time too */}
          <label className="consent">
            <input type="checkbox" checked={reg.whatsappOptIn} onChange={(e) => setReg((r) => ({ ...r, whatsappOptIn: e.target.checked }))} />
            <span>{t("about.consentTelegram")}</span>
          </label>
        </>
      )}
      {/* mockup field order (candidate-apply.html): name + birth date, ID + WhatsApp, then e-mail */}
      <div className="fld" style={{ margin: "12px 0 12px" }}>
        <label htmlFor="a_email">{t("about.email")}<Req /></label>
        <input className="in" id="a_email" type="email" value={reg.email} onChange={(e) => setReg((r) => ({ ...r, email: e.target.value }))} />
      </div>
      {/* The "shown-to" scope is applied by the caller — questions arrive pre-filtered. */}
      {questions.map((q) => {
        // Answer types (PRD §4.11, 2026-10-01): yes/no · document upload · free text · list of answers (§21.6).
        const answerType: 0 | 1 | 2 | 3 = q.AnswerType ?? (q.RequiresDocumentUpload ? 1 : 0);
        if (answerType === 3) {
          return (
            <div className="qrow listq" key={q.Id}>
              <div className="qtext" id={`q_${q.Id}`}>{q.QuestionText}<Req /></div>
              <div className="yn choices" role="radiogroup" aria-labelledby={`q_${q.Id}`}>
                {(q.Options ?? []).map((o) => (
                  <button
                    type="button"
                    key={o.Id}
                    role="radio"
                    aria-checked={reg.answers[q.Id] === o.Id}
                    className={reg.answers[q.Id] === o.Id ? "on" : ""}
                    onClick={() => setReg((r) => ({ ...r, answers: { ...r.answers, [q.Id]: o.Id } }))}
                  >
                    {o.Text}
                  </button>
                ))}
              </div>
            </div>
          );
        }
        return answerType === 1 ? (
          <div className="qrow docq" key={q.Id}>
            <div className="qtext">📎 {q.QuestionText}<Req /></div>
            <div className="docctl">
              {reg.files[q.Id] ? (
                <>
                  <span className="docfile">{reg.files[q.Id]!.name} ✓</span>
                  <button
                    type="button"
                    className="toplink"
                    onClick={() =>
                      setReg((r) => {
                        const { [q.Id]: _removed, ...restFiles } = r.files;
                        void _removed;
                        return { ...r, files: restFiles };
                      })
                    }
                  >
                    {t("about.remove")}
                  </button>
                </>
              ) : (
                <label className="btn ghost filebtn">
                  {q.DocumentType?.Name
                    ? t("about.attachType", { type: q.DocumentType.Name })
                    : t("about.attach")}
                  <input
                    type="file"
                    /* PRD §5.1 (2026-09-30): document-type questions accept a PDF or an image. */
                    accept=".pdf,application/pdf,image/png,image/jpeg,image/webp"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) setReg((r) => ({ ...r, files: { ...r.files, [q.Id]: f } }));
                    }}
                  />
                </label>
              )}
            </div>
          </div>
        ) : answerType === 2 ? (
          <div className="qrow textq" key={q.Id}>
            <div className="qtext">{q.QuestionText}<Req /></div>
            <input
              className="in"
              value={reg.answers[q.Id] ?? ""}
              onChange={(e) => setReg((r) => ({ ...r, answers: { ...r.answers, [q.Id]: e.target.value } }))}
            />
          </div>
        ) : (
          <div className="qrow" key={q.Id}>
            <div className="qtext">{q.QuestionText}<Req /></div>
            <div className="yn">
              <button
                type="button"
                className={reg.answers[q.Id] === "yes" ? "on" : ""}
                onClick={() => setReg((r) => ({ ...r, answers: { ...r.answers, [q.Id]: "yes" } }))}
              >
                {t("question.yes")}
              </button>
              <button
                type="button"
                className={reg.answers[q.Id] === "no" ? "on" : ""}
                onClick={() => setReg((r) => ({ ...r, answers: { ...r.answers, [q.Id]: "no" } }))}
              >
                {t("question.no")}
              </button>
            </div>
          </div>
        );
      })}
      {/* The agency's requested sign-up documents (PRD §4.12): optional & non-blocking —
          a candidate may attach them now or add them later from the portal. */}
      {docTypes.length > 0 && (
        <div className="docsblock">
          <div className="gh">{t("about.docsTitle")}</div>
          <div className="fsub" style={{ marginBottom: 8 }}>
            {t("about.docsSubtitle", { agency: agencyName })}
          </div>
          {docTypes.map((d) => (
            <div className="qrow docq" key={d.Id}>
              <div className="qtext">📄 {d.DocumentName}</div>
              <div className="docctl">
                {reg.agencyDocs[d.Id] ? (
                  <>
                    <span className="docfile">{reg.agencyDocs[d.Id]!.name} ✓</span>
                    <button
                      type="button"
                      className="toplink"
                      onClick={() =>
                        setReg((r) => {
                          const { [d.Id]: _removed, ...restDocs } = r.agencyDocs;
                          void _removed;
                          return { ...r, agencyDocs: restDocs };
                        })
                      }
                    >
                      {t("about.remove")}
                    </button>
                  </>
                ) : (
                  <label className="btn ghost filebtn">
                    {t("about.attach")}
                    <input
                      type="file"
                      accept=".pdf,application/pdf,image/png,image/jpeg,image/webp"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) setReg((r) => ({ ...r, agencyDocs: { ...r.agencyDocs, [d.Id]: f } }));
                      }}
                    />
                  </label>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="err">{err}</div>
      <div className="fnav">
        <button className="btn ghost" type="button" onClick={back}>
          {t("wizard.back")}
        </button>
        {submitting ? (
          <button className="btn" disabled>
            <span className="btnload" aria-hidden="true" /> {t("about.submitting")}
          </button>
        ) : (
          // no submit until every mandatory field (the asterisks) is filled
          <SubmitButton missing={aboutFormError(reg, questions)} />
        )}
      </div>
    </form>
  );
}

/** The About step's Submit: disabled while a mandatory field is missing, with a tooltip naming the
 *  first missing one. A disabled button gets no hover/focus, so the wrapper carries them — it is
 *  focusable while disabled so a tap (touch) or Tab (keyboard) shows the tooltip too. */
function SubmitButton({ missing }: { missing: TKey | null }) {
  if (!missing) {
    return <button className="btn">{t("about.submit")}</button>;
  }
  return (
    <span className="btntip" tabIndex={0} aria-describedby="submit-missing">
      <button className="btn" disabled>
        {t("about.submit")}
      </button>
      <span className="btntip-text" role="tooltip" id="submit-missing">
        {t(missing)}
      </span>
    </span>
  );
}

function VerifyStep({
  candidateId,
  whatsapp,
  email,
  chatbot = "whatsapp",
  telegramToken,
  telegramBotUsername,
  whatsappInboundNumber,
  onTelegramTokenChanged,
  onWhatsAppVerified,
  onEmailConfirmed,
  onErr,
  err,
  portalUrl,
  onDoneIntent,
  /** 2026-10-07: the in-step channel switch. */
  onSwitchChannel,
  /** The CHECK-STATUS entry (2026-10-07): the candidate's e-mail was never confirmed — the
   *  status links show the CONFIRM-EMAIL step instead of redirecting; the e-mail confirmation
   *  creates the account and sends the password-reset link (2026-10-02 ruling). */
  emailFocus = false,
  /** 2026-10 fix: the out-come of the Log-in-triggered resend, seeded at mount (null otherwise). */
  seedPasswordMail = null,
}: {
  candidateId: string | null;
  whatsapp: string;
  email: string;
  /** Chatbot PRD §7: the telegram choice swaps the code-entry card for the /start
   *  link-Telegram card (ownership proof by binding, no code typed in chat). */
  chatbot?: "whatsapp" | "telegram";
  telegramToken?: string | null;
  /** The effective bot @handle WITHOUT @ (2026-10-03 ruling): SERVER (GetSiteChatbotConfig)
   *  first, VITE_TELEGRAM_BOT_USERNAME env fallback second — never hardcoded. */
  telegramBotUsername?: string;
  /** THE INBOUND NUMBER (chatbot PRD §15, T-SA-18.18): the WABA sender digits the candidates
   *  message — the wa.me handshake addresses THE CHATBOT, never the candidate. Empty (an
   *  unconfigured deployment) falls back to the candidate's number (the pre-18.18 behavior). */
  whatsappInboundNumber?: string;
  onTelegramTokenChanged?: (token: string | null) => void;
  /** 2026-10-08: an optional "who just confirmed" tag — "telegram" (the bound paths),
   *  "whatsapp" (the code confirmed), "none" (the candidate skipped both) — the done step
   *  names the linked messenger in its reach-out wording (and toasts accordingly). */
  onWhatsAppVerified: (messenger?: "whatsapp" | "telegram" | "none") => void;
  /** 2026-10-09: the in-step e-mail confirmation succeeded — the page-level alert turns green. */
  onEmailConfirmed?: () => void;
  onErr: Dispatch<SetStateAction<string>>;
  err: string;
  /** The internal-site URL (for the password-reset wording; the Done flow's own target). */
  portalUrl?: string;
  emailFocus?: boolean;
  /** 2026-10 fix: the outcome of the Log-in-triggered resend, seeded as the initial state so
   *  the e-mail-focused view shows it at once (null = unknown, no seed). */
  seedPasswordMail?: PasswordMailStatus | null;
  onDoneIntent?: () => void;
  /** The in-step CHANNEL SWITCH (2026-10-07): "confirm via the OTHER messenger instead" —
   *  the OTHER channel's confirmation card renders in place; both channels can end confirmed. */
  onSwitchChannel?: (to: "whatsapp" | "telegram") => void;
}) {
  const [busy, setBusy] = useState(false);
  // T-SA-18.14: the password-setup mail outcome of THIS session's confirmation / resend
  // (null = unknown, e.g. the page was reloaded after an earlier confirmation)
  const [passwordMail, setPasswordMail] = useState<PasswordMailStatus | null>(seedPasswordMail);
  const [passwordBusy, setPasswordBusy] = useState(false);

  // ---------------------------------------------------------------------------
  // TELEGRAM PRIMARY (chatbot PRD §5.2): the /start deep-link binding replaces the
  // code entry - no code is typed in chat, the BINDING is the ownership proof. The
  // page shows the link + polls the binding status; 'skip' leaves the link for
  // later ("Check my status" re-runs this flow). The bot @handle arrives from the
  // SERVER (GetSiteChatbotConfig; 2026-10-03) - the VITE env var is only a fallback.
  // ---------------------------------------------------------------------------
  const [tgBound, setTgBound] = useState(false);
  const [tgToken, setTgToken] = useState<string | null>(telegramToken ?? null);
  // THE TWO-STEP TELEGRAM CONFIRMATION (2026-10-10): 'awaiting_phone' = the /start binding is
  // consumed (the Telegram Id was received by the server — never shown here) but the PHONE
  // NUMBER was not shared in the chat yet. The page shows the phone-consent block (the button
  // below re-sends the share request into the chat) and keeps polling until 'bound'.
  const [tgWaitingPhone, setTgWaitingPhone] = useState(false);
  const [tgPhoneReqBusy, setTgPhoneReqBusy] = useState(false);
  useEffect(() => {
    if (chatbot !== "telegram" || !candidateId) return;
    if (!telegramBotUsername) {
      onErr(t("verify.telegram.err.botConfig"));
      return;
    }

    let cancelled = false;
    let timer: number | undefined;

    // ALREADY-VERIFIED PROBE (2026-10-07): re-entering "Verify me"/refreshing the page with the
    // binding ALREADY in place (a previous session completed it) must NOT re-issue tokens — the
    // token-less candidate probe answers "bound" and the card jumps straight to the Done state.
    getTelegramBindingStatus(candidateId, "")
      .then((probe) => {
        if (cancelled) return;
        if (probe?.Bound && !emailFocus) {
          // (2026-10-29, the staging finding): the jump-to-done only in the REGISTRATION mode —
          // an E-MAIL-focused entry (the Verify-me click with EmailValidated=false, the exact
          // state the "confirm your e-mail" hint shows) must render the e-mail card instead
          setTgBound(true);
          if (probe.EmailValidated) setEmailWasValidated(true);
          toast(t("verify.telegram.toast.linked"));
          onWhatsAppVerified("telegram");
          return;
        }
        if (probe) setEmailWasValidated(probe.EmailValidated ?? false);
      })
      .catch(() => {
        // the probe is best-effort — the normal token flow continues regardless
      });
    // The AWAITING-PHONE poll runs on an EXTENDED budget: the two-step confirmation is a
    // deliberate extra step (the candidate may read the chat at their own pace) — the base
    // 100-iteration budget applies until the phone-consent wait begins, then the budget is
    // extended 10× (30 minutes at the 3-second cadence).
    let attempts = 0;
    let phoneWait = false;
    const BASE_ATTEMPTS = 100;
    const PHONE_WAIT_ATTEMPTS = 1000;

    // STALE-TOKEN GUARD (2026-10-07): the card may carry a token whose binding row is ALREADY
    // EXPIRED/missing (a later re-entry, the old server build) — such a (candidateId, token)
    // pair can NEVER bind: the poll reports expired/unknown forever. The counter bounds the
    // automatic re-issue.
    let reissues = 0;
    const REISSUE_LIMIT = 3;

    // THE TOKEN CORRECTNESS (the 2026-10-07 b fix): ALWAYS track the LATEST token in a plain
    // local variable. The previous version read the closure-snapshot state (tgToken/telegramToken)
    // which NEVER updated inside a running poll loop — every iteration minted ANOTHER binding
    // token (the /link code kept changing on screen) and the poll polled a different token than
    // the one displayed/bound, so the page never flipped to Done.
    let currentToken: string | null = telegramToken ?? null;

    const adoptToken = (token: string): string => {
      currentToken = token;
      setTgToken(token);
      onTelegramTokenChanged?.(token);
      return token;
    };

    const issueToken = async (): Promise<string | null> => {
      if (cancelled) return null;
      try {
        const token = await requestTelegramBinding(candidateId);
        if (cancelled || !token) return null;
        return adoptToken(token);
      } catch {
        return null;
      }
    };

    const ensureToken = async (): Promise<string | null> =>
      currentToken ?? (await issueToken());

    const reissueToken = (): Promise<string | null> => issueToken();

    const poll = async (): Promise<void> => {
      const token = await ensureToken();
      if (cancelled || !token) return;
      try {
        const status = await getTelegramBindingStatus(candidateId, token);
        if (!cancelled && status?.Bound && !emailFocus) {
          // (2026-10-29, the staging finding): the jump stays OUT of the e-mail-focused entry
          setTgBound(true);
          toast(t("verify.telegram.toast.linked"));
          onWhatsAppVerified("telegram");
          return;
        }
        if (!cancelled && status?.Status === "awaiting_phone") {
          // STEP 1 done (the Telegram Id received, the page never sees it) — the phone share
          // consent is pending: show the [Confirm phone number by Telegram] button and keep
          // polling; the poll flips on the phone share confirmation (the server-side row).
          setTgWaitingPhone(true);
          phoneWait = true;
        }
        // The polled row can never bind (expired / vanished) — re-issue a FRESH token (bounded)
        // and keep polling it (the display adopts the fresh token via adoptToken above).
        if (
          !cancelled &&
          reissues < REISSUE_LIMIT &&
          (status?.Status === "expired" || status?.Status === "unknown")
        ) {
          reissues += 1;
          const fresh = await reissueToken();
          if (!cancelled && fresh) {
            timer = window.setTimeout(poll, 3000);
            return;
          }
        }
      } catch {
        // transient polling errors - keep polling until the attempt budget runs out
      }
      attempts += 1;
      if (!cancelled && attempts <= (phoneWait ? PHONE_WAIT_ATTEMPTS : BASE_ATTEMPTS)) {
        timer = window.setTimeout(poll, 3000);
      }
    };

    if (currentToken) {
      void poll();
    } else {
      void ensureToken().then((token) => {
        if (!cancelled && token) void poll();
      });
    }

    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatbot, candidateId]);

  // Optional e-mail verification (2026-10-02, §13 row 43): the candidate verifies at will;
  // skipping simply leaves the e-mail unvalidated (verifiable later via "Verify me"/portal).
  const [emailSent, setEmailSent] = useState(false);
  const [emailCode, setEmailCode] = useState("");
  const [emailVerified, setEmailVerified] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);
  // the ALREADY-CONFIRMED e-mail (the candidate-level probe) — null = the probe has no answer
  const [emailWasValidated, setEmailWasValidated] = useState<boolean | null>(null);
  // the WhatsApp confirmation from the same probe (null = no answer yet) — the confirmed-e-mail
  // view prompts "Confirm WhatsApp" while it is still pending (mandatory since §18.2)
  const [whatsappWasValidated, setWhatsappWasValidated] = useState<boolean | null>(null);

  // ---------------------------------------------------------------------------
  // TELEGRAM QR (2026-10-06, the desktop-registration enhancement): the SAME /start deep
  // link (SERVER-delivered bot handle) is ALSO rendered as a scannable QR so a user
  // registering on a computer can scan it with the phone (camera) and press START there.
  // Fully client-side generation (`qrcode` lib, PNG data URL): no third-party QR web
  // service - the binding token never crosses the site's own origins. Hooks stay at the
  // top of the component (unconditional - the rules of hooks).
  // ---------------------------------------------------------------------------
  const telegramLink =
    chatbot === "telegram" && (tgToken ?? telegramToken) && telegramBotUsername
      ? `https://t.me/${telegramBotUsername}?start=${(tgToken ?? telegramToken)!}`
      : null;
  const [qr, setQr] = useState<string | null>(null);
  useEffect(() => {
    if (!telegramLink) {
      setQr(null);
      return;
    }
    let cancelled = false;
    QRCode.toDataURL(telegramLink, { margin: 1, width: 220, errorCorrectionLevel: "M" })
      .then((url) => {
        if (!cancelled) setQr(url);
      })
      .catch(() => {
        if (!cancelled) setQr(null); // QR is an enhancement - the button alone must keep working
      });
    return () => {
      cancelled = true;
    };
  }, [telegramLink]);

  // THE WA.ME HANDSHAKE (chatbot PRD §15 / Amendment 1.3): NO code is requested or typed —
  // the page issues the click-to-chat link token and the CANDIDATE INITIATES (the prefilled
  // message). The poll reuses the token-LESS candidate-level probe (WhatsAppValidated);
  // a stale/expired token gets a bounded re-issue (3), mirroring the Telegram poll.
  const [waLinkToken, setWaLinkToken] = useState<string | null>(null);
  const waLinkTokenRef = useRef<string | null>(null);
  const issueWaLink = useCallback(async (): Promise<string | null> => {
    if (!candidateId) return null;
    try {
      const token = await requestWhatsAppLink(candidateId, whatsapp);
      waLinkTokenRef.current = token;
      setWaLinkToken(token);
      return token;
    } catch (e) {
      onErr(e instanceof Error ? e.message : t("verify.err.link"));
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateId, whatsapp]);
  /** The manual re-issue (the resend button): resets the poll budget; bounded by the same
   *  poll re-issues overall. */
  const waAttemptsRef = useRef(0);
  const fetchWaLink = useCallback(() => {
    waAttemptsRef.current = 0;
    return issueWaLink();
  }, [issueWaLink]);

  // THE WA.ME QR (§15, the parity of the Telegram QR): the SAME click-to-chat link as a
  // scannable PNG for desktop registration — fully client-side (`qrcode`), the token never
  // crosses third-party origins.
  // THE WA.ME TARGET (T-SA-18.18): the AGENCY'S chatbot number (the WABA sender the candidates
  // message) — the candidate's own number only as the unconfigured-deployment fallback
  const waTargetDigits =
    (whatsappInboundNumber && whatsappInboundNumber.length >= 8
      ? whatsappInboundNumber
      : whatsapp).replace(/\D/g, "");
  const whatsappLink =
    chatbot === "whatsapp" && waLinkToken && waTargetDigits
      ? `https://wa.me/${waTargetDigits}?text=${encodeURIComponent(waLinkToken)}`
      : null;
  const [waQr, setWaQr] = useState<string | null>(null);
  useEffect(() => {
    if (!whatsappLink) {
      setWaQr(null);
      return;
    }
    let cancelled = false;
    QRCode.toDataURL(whatsappLink, { margin: 1, width: 220, errorCorrectionLevel: "M" })
      .then((url) => {
        if (!cancelled) setWaQr(url);
      })
      .catch(() => {
        if (!cancelled) setWaQr(null); // QR is an enhancement - the button alone must keep working
      });
    return () => {
      cancelled = true;
    };
  }, [whatsappLink]);

  useEffect(() => {
    if (!candidateId) return;
    let cancelled = false;
    // THE CANDIDATE-LEVEL PROBE rides the PROPERLY-NAMED chatbot endpoint (T-SA-18.19) — the
    // WhatsApp verify cards must not poll a Telegram-named route
    getChatbotBindingStatus(candidateId)
      .then((probe) => {
        if (cancelled || !probe) return;
        if (probe.WhatsAppValidated && !emailFocus) {
          onWhatsAppVerified("whatsapp");
          return;
        }
        setEmailWasValidated(probe.EmailValidated ?? false);
        setWhatsappWasValidated(probe.WhatsAppValidated === true);
      })
      .catch(() => {
        // best-effort: the normal code flow continues when the probe cannot answer
      });
    return () => {
      cancelled = true;
    };
  }, [candidateId]);

  useEffect(() => {
    // (2026-10-29, the staging finding part 3): while the candidate is in the E-MAIL-FOCUSED
    // view (the Verify-me click with EmailValidated=false), the WHATSAPP poll effect must NOT
    // run at all: it re-issues the wa.me link (RequestWhatsAppLink noise in the console) and
    // its WhatsAppValidated probe jumps straight to Done — swallowing the e-mail card even
    // after the tap-routing guards. The email-focus view's own probe + the wapending button
    // handle the channel properly.
    if (!candidateId || emailFocus || chatbot !== "whatsapp" || !whatsapp) return;
    let cancelled = false;
    let timer: number | undefined;
    let reissues = 0;
    const REISSUE_LIMIT = 3;
    const poll = async (): Promise<void> => {
      // DEMO: the candidate-level probe never flips (no live webhook) — the card stays and the
      // OPEN-LINK click acts as the candidate's send (see the card's demo branch below).
      try {
        const status = await getChatbotBindingStatus(candidateId);
        if (!cancelled && status?.WhatsAppValidated) {
          toast(t("verify.whatsapp.done"));
          onWhatsAppVerified("whatsapp");
          return;
        }
      } catch {
        // best-effort polling - transient errors keep the loop
      }
      waAttemptsRef.current += 1;
      // 100 iterations ≈ 5 min at the 3-second cadence; past that re-issue the (expired)
      // link row bounded times and keep polling the fresh one
      if (waAttemptsRef.current > 100 && waLinkTokenRef.current) {
        if (reissues >= REISSUE_LIMIT) {
          if (!cancelled) setWaLinkToken(null);
          return;
        }
        reissues += 1;
        waAttemptsRef.current = 0;
        await issueWaLink();
      }
      if (!cancelled) timer = window.setTimeout(() => void poll(), 3000);
    };
    setBusy(true);
    void issueWaLink().then(() => {
      if (!cancelled) setBusy(false);
      void poll();
    });
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateId, emailFocus, chatbot, whatsapp, issueWaLink]);
  if (!candidateId) return null;



  const requestEmailCode = async () => {
    if (!candidateId) return;
    if (!/^\S+@\S+\.\S+$/.test(email)) return onErr(t("email.err.first"));
    onErr("");
    setEmailBusy(true);
    try {
      await requestContactCode(candidateId, ContactChannels.Email, email);
      setEmailSent(true);
      toast(t("email.toast.sent"));
    } catch (e) {
      onErr(e instanceof Error ? e.message : t("email.err.send"));
    } finally {
      setEmailBusy(false);
    }
  };

  const confirmEmail = async () => {
    if (!candidateId) return;
    if (!/^\d{6}$/.test(emailCode)) return onErr(t("email.err.code"));
    onErr("");
    setEmailBusy(true);
    try {
      const mailStatus = await confirmContactCode(candidateId, ContactChannels.Email, email, emailCode);
      setPasswordMail(mailStatus);
      setEmailVerified(true);
      onEmailConfirmed?.();
      toast(t("email.toast.verified"));
    } catch (e) {
      onErr(e instanceof Error ? e.message : t("email.err.confirm"));
    } finally {
      setEmailBusy(false);
    }
  };

  // T-SA-18.14: the confirmed e-mail no longer offers the code form - a missing password
  // link (not sent, lost, expired) is requested again here
  const resendPassword = async () => {
    if (!candidateId) return;
    onErr("");
    setPasswordBusy(true);
    try {
      const mailStatus = await resendPasswordLink(candidateId, email);
      setPasswordMail(mailStatus);
      if (mailStatus === "Sent") toast(t("email.password.toast.resent", { email: email || t("about.email") }));
    } catch (e) {
      onErr(e instanceof Error ? e.message : t("email.password.err.resend"));
    } finally {
      setPasswordBusy(false);
    }
  };

  // ALREADY-VERIFIED PROBE (2026-10-07; consolidated 2026-10-07b): ONE token-less candidate
  // read answers all three channels — the re-entered verify step then shows
  // "already verified" states instead of asking for another code.


  // THE E-MAIL-FIRST ORDER (chatbot PRD Amendment 1.4 rule 5, approved 2026-10-23/29): when
  // the probe says the e-mail is NOT confirmed, the E-MAIL card comes FIRST (the account
  // identity gates the messenger confirmations server-side too) - the messenger cards render
  // only after the e-mail confirm. emailFocus (the Check-status/Verify-me entry) forces the
  // same view at once.
  if (emailFocus || (emailWasValidated === false && !emailVerified)) {
    // the CHECK-STATUS entry OR the unconfirmed-e-mail candidate: e-mail confirmation only
    // (the account + the reset link) — the WhatsApp/Telegram cards follow after the confirm.
    return (
      <section className="funnel">
        {/* "One step to finish: confirm your e-mail…" only while the e-mail is KNOWN to be
            unconfirmed — a confirmed one (e.g. the header's Password link) has nothing left to
            finish; awaiting the probe (null) shows nothing, so a confirmed candidate never sees
            it flash. */}
        {!emailVerified && emailWasValidated === false && (
          <div className="fsub">{t("email.focusNote")}</div>
        )}
        {true && (
          <div className="emailverify">
            <div className="fh1 emailtitle">
              {(emailVerified || emailWasValidated === true)
                ? t("email.titleConfirmed")
                : t("email.confirmTitle")}
            </div>
            {!(emailVerified || emailWasValidated === true) && (
              <div className="fsub">
                {emailSent
                  ? t("email.sent", { email: email || t("about.email") })
                  : t("email.intro", { email: email || t("about.email") })}
              </div>
            )}
            {(emailVerified || emailWasValidated === true) && (
              <PasswordLinkNote
                status={passwordMail} email={email} portalUrl={portalUrl}
                busy={passwordBusy} onResend={() => void resendPassword()}
              >
                <button
                  type="button"
                  className="btn"
                  onClick={() => {
                    onDoneIntent?.();
                    onWhatsAppVerified();
                  }}
                >
                  {t("email.continueToStatus")}
                </button>
              </PasswordLinkNote>
            )}
            {/* the "Verify me" action, MOVED here from the header (T-SA-19.01): on this view the
                e-mail is done — what may be left is the WhatsApp number (mandatory, §18.2); the
                button opens the WhatsApp code entry in place. Hidden once WhatsApp is confirmed. */}
            {(emailVerified || emailWasValidated === true) && whatsappWasValidated === false && (
              <div className="wapending">
                <div className="fsub">{t("email.whatsappPending")}</div>
                <button
                  type="button"
                  className="btn"
                  onClick={() => {
                    onSwitchChannel?.("whatsapp");
                    onDoneIntent?.(); // leaves the e-mail-only view → the WhatsApp code card
                  }}
                >
                  {t("email.confirmWhatsapp")}
                </button>
              </div>
            )}
            {!emailVerified && emailWasValidated !== true && (
              <>
                {emailSent && (
                  <div className="fld" style={{ maxWidth: 240 }}>
                    <input
                      className="in"
                      inputMode="numeric"
                      pattern="\d{6}"
                      maxLength={6}
                      placeholder="• • • • • •"
                      value={emailCode}
                      onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, ""))}
                    />
                  </div>
                )}
                <div className="fnav">
                  <button type="button" className="btn" disabled={emailBusy} onClick={() => void (emailSent ? confirmEmail() : requestEmailCode())}>
                    {emailSent ? t("email.verify") : t("email.send")}
                  </button>
                  {emailSent && (
                    <button
                      type="button"
                      className="btn ghost"
                      disabled={emailBusy}
                      onClick={() => void requestEmailCode().then(() => toast(t("email.toast.sent")))}
                    >
                      {t("email.resend")}
                    </button>
                  )}
                </div>
              </>
            )}
            <div className="err">{""}</div>
          </div>
        )}
        <div className="err">{err}</div>
      </section>
    );
  }

  if (chatbot === "telegram") {
    // Chatbot PRD §5.2/§7: the Telegram-primary candidate LINKS the chat (see above) -
    // and then CONFIRMS THE PHONE NUMBER (the two-step confirmation, 2026-10-10):
    // step 1 = the /start binding (the ownership proof), step 2 = the share-phone consent
    // in the chat — the registration completes only after the phone number arrives.
    return (
      <section className="funnel">
        {/* 2026-10-08: the EMAIL card = the absolute top (the account + the reset link);
            "Link your Telegram" heading follows right after it. */}
            <EmailCard
              emailOpen={true} emailSent={emailSent} emailCode={emailCode}
              emailVerified={emailVerified} emailWasValidated={emailWasValidated}
              emailBusy={emailBusy} email={email} portalUrl={portalUrl}
              passwordMail={passwordMail} passwordBusy={passwordBusy}
              onPasswordResend={() => void resendPassword()}
              onEmailCodeChange={(v) => setEmailCode(v)}
              onEmailSend={() => void (emailSent ? confirmEmail() : requestEmailCode())}
              onEmailResend={() => void requestEmailCode().then(() => toast(t("email.toast.sent")))}
              />
        <div className="fh1">{t("verify.telegram.title")}</div>

        {tgBound ? (
          <div className="fsub">{t("verify.telegram.done")}</div>
        ) : (
          <>
            <div className="fsub">{t("verify.telegram.body")}</div>
            {telegramLink ? (
              <>
                <div className="fnav">
                  <a className="btn" href={telegramLink} target="_blank" rel="noreferrer">
                    {t("verify.telegram.openLink")}
                  </a>
                </div>
                {/* REGISTRATION-ON-COMPUTER: scan with the phone's camera, press START there */}
                {qr && (
                  <div className="qrbox">
                    <img src={qr} alt={t("verify.telegram.openLink")} width={220} height={220} />
                    <div className="fsub">{t("verify.telegram.qrCaption")}</div>
                  </div>
                )}
                {/* TYPED /link fallback (2026-10-06): Telegram only PREFILLS /start {token} on a
                    brand-new chat — a chat that already existed opens bare and cannot bind. The
                    SHORT linking code (2026-10-06 ruling: 8 chars, digits + lowercase letters —
                    the token prefix already is) is typed into the bot instead: /link {code} from
                    ANY chat completes the binding; a mistyped code is rate-limited server-side. */}
                {(telegramToken ?? tgToken) != null && (
                  <div className="linkcode">
                    <div className="fsub">{t("verify.telegram.linkCode.title")}</div>
                    <div className="fsub">{t("verify.telegram.linkCode.body")}</div>
                    <code><b>/link {((tgToken ?? telegramToken) || "").slice(0, 8)}</b></code>
                  </div>
                )}
              </>
            ) : (
              <div className="fsub">{t("verify.telegram.preparing")}</div>
            )}
            <div className="fsub" style={{ marginTop: 8 }}>{t("verify.telegram.waiting")}</div>
            {tgWaitingPhone && (
              /* THE TWO-STEP CONFIRMATION (2026-10-10): the Telegram Id was received (never
                 revealed here) — the PHONE NUMBER is still pending: the consent block with the
                 re-send button (the server re-delivers the share-phone message into the chat). */
              <div className="emailverify" style={{ marginTop: 12 }}>
                <div className="fh1 emailtitle">{t("verify.telegram.phoneTitle")}</div>
                <div className="fsub">{t("verify.telegram.phoneBody")}</div>
                <div className="fnav">
                  <button
                    type="button"
                    className="btn"
                    disabled={tgPhoneReqBusy}
                    onClick={() => {
                      setTgPhoneReqBusy(true);
                      onErr("");
                      resendTelegramPhoneRequest(candidateId, tgToken ?? telegramToken ?? null)
                        .then(() => toast(t("verify.telegram.phoneToast")))
                        .catch((e) =>
                          onErr(e instanceof Error ? e.message : t("verify.telegram.phoneBody")))
                        .finally(() => setTgPhoneReqBusy(false));
                    }}
                  >
                    {t("verify.telegram.phoneBtn")}
                  </button>
                </div>
                <div className="err">{""}</div>
              </div>
            )}
            <div className="fsub">{t("verify.waitingNote")}</div>
            <div className="fnav">
              <button className="btn ghost" type="button" onClick={() => onWhatsAppVerified("none")}>
                {t("verify.telegram.skip")}
              </button>
            </div>
            {/* 2026-10-07: the CHANNEL SWITCH — confirm via WhatsApp instead (the phone code card),
                and the E-MAIL confirmation card remains available at every stage of the flow. */}
            <ChatSwitch onSwitch={() => onSwitchChannel?.("whatsapp")} to="whatsapp" />
          </>
        )}
        <div className="err">{err}</div>
      </section>
    );
  }

  return (
    <section className="funnel">
      {/* 2026-10-08: the EMAIL card = the absolute top (the account + the reset link);
          "Confirm your WhatsApp number" heading follows right after it. */}
      <EmailCard
        emailOpen={true} emailSent={emailSent} emailCode={emailCode}
        emailVerified={emailVerified} emailWasValidated={emailWasValidated}
        emailBusy={emailBusy} email={email} portalUrl={portalUrl}
        passwordMail={passwordMail} passwordBusy={passwordBusy}
        onPasswordResend={() => void resendPassword()}
        onEmailCodeChange={(v) => setEmailCode(v)}
        onEmailSend={() => void (emailSent ? confirmEmail() : requestEmailCode())}
        onEmailResend={() => void requestEmailCode().then(() => toast(t("email.toast.sent")))}
        />
      <div className="fh1">{t("verify.title")}</div>

      <div className="fsub">
        {t("verify.body", { whatsapp })}
      </div>
      {/* THE WA.ME LINK CARD (chatbot PRD §15 / Amendment 1.3): the candidate INITIATES - the
          open link / the QR open a WhatsApp chat with the CONFIRMATION TOKEN prefilled; the
          webhook completes the confirmation and the candidate-level poll flips the page. */}
      {waLinkToken ? (
        <>
          <div className="fnav">
            <a
              className="btn"
              href={`https://wa.me/${waTargetDigits}?text=${encodeURIComponent(waLinkToken)}`}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => {
                // DEMO: no live webhook — the click IS the candidate's send; flip after a tick
                if (!isLiveApi) {
                  e.preventDefault(); // nothing to open in the demo
                  window.setTimeout(() => {
                    toast(t("verify.whatsapp.done"));
                    onWhatsAppVerified("whatsapp");
                  }, 1200);
                }
              }}
            >
              {t("verify.whatsapp.openLink")}
            </a>
          </div>
          {waQr && (
            <div className="qrbox">
              <img src={waQr} alt={t("verify.whatsapp.openLink")} width={220} height={220} />
              <div className="fsub">{t("verify.whatsapp.qrCaption")}</div>
            </div>
          )}
          <div className="fsub">{t("verify.whatsapp.waiting")}</div>
          <div className="fnav">
            <button
              className="btn ghost resendbtn"
              disabled={busy}
              onClick={() => void fetchWaLink()}
            >
              {t("verify.resend")}
            </button>
          </div>
        </>
      ) : (
        <div className="fsub">{busy ? t("verify.whatsapp.preparing") : t("verify.waitingNote")}</div>
      )}
      <div className="err">{err}</div>
      {/* 2026-10-22: no "I'd rather confirm via Telegram" switch here — WhatsApp is the only
          channel offered for now (T-SA-23.03). */}
    </section>
 
  );
}

function Done({
  agencyName,
  candidateName,
  messenger,
  onHome,
  statusLinksEnabled,
  onCheckStatus,
}: {
  agencyName: string;
  candidateName?: string;
  /** 2026-10-08: the messenger the candidate LINKED ("telegram"/"whatsapp"); null when
   *  nothing confirmed → the neutral "your linked messenger" wording. */
  messenger?: "whatsapp" | "telegram" | null;
  onHome: () => void;
  /** null = still probing (the links stay disabled meanwhile) (2026-10-08). */
  statusLinksEnabled: boolean | null;
  /** The gated status link: probe → redirect or the confirm-email step (2026-10-07). */
  onCheckStatus: () => void;
}) {
  const firstName = (candidateName ?? "").trim().split(/\s+/)[0] ?? "";
  return (
    <section className="funnel">
      <div className="donebox">
        <div className="tickwrap">
          <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <path d="m5 12 5 5L19 7" />
          </svg>
        </div>
        {/* the confirmed e-mail alert sits right under the check mark (2026-11-02) */}
        {statusLinksEnabled === true && (
          <VerifyHint
            variant="success"
            title={t("header.emailConfirmedTitle")}
            hint={t("header.emailConfirmedHint")}
          />
        )}
        <div className="fh1">
          {firstName ? t("done.thanks", { name: firstName }) : t("done.thanksNoName")}
        </div>
        <p className="fsub">
          {t("done.body", {
            agency: agencyName,
            messenger:
              messenger === "telegram"
                ? "Telegram"
                : messenger === "whatsapp"
                  ? "WhatsApp"
                  : t("done.messengerAny"),
          })}
        </p>
        {/* Back-to-home on the LEFT, Check-my-status on the RIGHT (the portal opens in
            another tab so the done state stays — 2026-10-02) */}
        <div className="fnav">
          <button className="btn ghost" type="button" onClick={onHome}>
            ← {t("done.backToHome")}
          </button>
          <button
            className="btn"
            type="button"
            /* ALWAYS clickable: the redirect to the agency portal (the candidate login);
               the unconfirmed-e-mail warning renders below (the probe informs it only) */
            title={t("header.loginTooltip")}
            onClick={onCheckStatus}
          >
            {t("done.cta")}
          </button>
        </div>
        {/* the submitted-registration hint (the e-mail unconfirmed → the account/confirm needed) */}
        {statusLinksEnabled === false && <VerifyHint title={t("header.registerToLogin")} />}
      </div>
    </section>
  );
}

/**
 * 2026-10-09: the unconfirmed-e-mail callout (the header banner + the Done screen) — the
 * warning as the bold line, the click-'Verify me' instruction under it and, when the candidate
 * can act from here, the "Verify me" (blue) + "Forget my data" (red) actions INSIDE the alert
 * (the header account actions then hide).
 */
function VerifyHint({
  title,
  hint,
  variant = "warning",
  verifyLabel,
  onVerifyMe,
  onForgetMyData,
}: {
  title: string;
  /** the second line (default: the click-'Verify me' instruction). */
  hint?: string;
  /** 2026-10-09: "success" = the confirmed e-mail (green, the check icon). */
  variant?: "warning" | "success";
  verifyLabel?: string;
  onVerifyMe?: () => void;
  onForgetMyData?: () => void;
}) {
  return (
    <div className={variant === "success" ? "hcallout ok" : "hcallout"} role="note">
      <span className="hcalloutico" aria-hidden="true">
        {variant === "success" ? (
          <svg viewBox="0 0 24 24" width="18" height="18">
            <path d="m5 12 5 5L19 7" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" width="18" height="18">
            <rect x="3" y="5" width="18" height="14" rx="2" fill="none" stroke="currentColor" strokeWidth="2" />
            <path d="m3 7 9 6 9-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </span>
      <span className="hcallouttxt">
        <b>{title}</b>
        <span>{hint ?? t("header.confirmEmailActionHint")}</span>
      </span>
      {onVerifyMe || onForgetMyData ? (
        <span className="hcalloutacts">
          {onVerifyMe ? (
            <button type="button" className="hcalloutbtn" onClick={onVerifyMe}>
              <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
                <path d="M12 2 4 5v6c0 5 3.4 9.4 8 11 4.6-1.6 8-6 8-11V5l-8-3Z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                <path d="m8.5 12 2.5 2.5 4.5-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {verifyLabel ?? t("header.verifyMe")}
            </button>
          ) : null}
          {onForgetMyData ? <ForgetMyDataButton onForgetMyData={onForgetMyData} /> : null}
        </span>
      ) : null}
    </div>
  );
}

/** 2026-10-08: the "Verify me — Juan G." label helper (the name + the LAST name's first letter). */
function verifyMeName(fullName: string): string {
  const parts = (fullName || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1]![0].toUpperCase()}.`;
}

/** 2026-10-07: the in-step CHANNEL SWITCH ("I'd rather confirm via …") — renders the other
 *  channel's confirmation card in place; both channels can end confirmed. */
function ChatSwitch({ onSwitch, to }: { onSwitch: () => void; to: "whatsapp" | "telegram" }) {
  return (
    <div className="skiprow">
      <button type="button" className="toplink" onClick={onSwitch}>
        {to === "telegram" ? t("verify.switchToTelegram") : t("verify.switchToWhatsapp")}
      </button>
    </div>
  );
}

/** 2026-10-07: the SHARED e-mail confirmation card — rendered under BOTH the WhatsApp and the
 *  Telegram confirmation panels: the account + the password-reset link happen here, so the card
 *  is offered (recommended) whenever the e-mail is not confirmed yet, shows "already confirmed"
 *  when it is, and notes the sent password link after an in-session confirmation. */
function EmailCard(props: {
  emailOpen: boolean;
  emailSent: boolean;
  emailCode: string;
  emailVerified: boolean;
  emailWasValidated: boolean | null;
  emailBusy: boolean;
  email: string;
  portalUrl?: string;
  passwordMail: PasswordMailStatus | null;
  passwordBusy: boolean;
  onPasswordResend: () => void;
  onEmailCodeChange: (value: string) => void;
  onEmailSend: () => void;
  onEmailResend: () => void;
}) {
  const {
    emailOpen, emailSent, emailCode, emailVerified, emailWasValidated,
    emailBusy, email, portalUrl, passwordMail, passwordBusy,
    onPasswordResend, onEmailCodeChange, onEmailSend, onEmailResend,
  } = props;
  if (!emailOpen) return null;
  // 2026-10-08 (staging feedback): the "Verify your e-mail — necessary…" heading is MISLEADING
  // once the e-mail IS confirmed — show the confirmed state instead.
  const confirmed = emailVerified || emailWasValidated === true;
  return (
    <div className="emailverify">
      <div className="fh1 emailtitle">{confirmed ? t("email.titleConfirmed") : t("email.title")}</div>
      {/* 2026-10-08: in the confirmed state the title says it all — the body line below
          ("Your e-mail is verified/already confirmed ✓") would DUPLICATE the heading;
          the password-note that follows is the real information. */}
      {!confirmed && (
        <div className="fsub">
          {emailSent
            ? t("email.sent", { email: email || t("about.email") })
            : t("email.intro", { email: email || t("about.email") })}
        </div>
      )}
      {!emailVerified && emailWasValidated !== true && (
        <div className="fsub">{t("email.recommended")}</div>
      )}
      {(emailVerified || emailWasValidated === true) && (
        <PasswordLinkNote
          status={passwordMail} email={email} portalUrl={portalUrl}
          busy={passwordBusy} onResend={onPasswordResend}
        />
      )}
      {emailSent && !emailVerified && emailWasValidated !== true && (
        <div className="fld" style={{ maxWidth: 240 }}>
          <input
            className="in"
            inputMode="numeric"
            pattern="\d{6}"
            maxLength={6}
            placeholder="• • • • • •"
            value={emailCode}
            onChange={(e) => onEmailCodeChange(e.target.value.replace(/\D/g, ""))}
          />
        </div>
      )}
      {!emailVerified && emailWasValidated !== true && (
        <div className="fnav">
          <button type="button" className="btn" disabled={emailBusy} onClick={onEmailSend}>
            {emailSent ? t("email.verify") : t("email.send")}
          </button>
          {emailSent && (
            <button type="button" className="btn ghost" disabled={emailBusy} onClick={onEmailResend}>
              {t("email.resend")}
            </button>
          )}
        </div>
      )}

      <div className="err">{""}</div>
    </div>
  );
}

/** The password-setup link note of a confirmed e-mail (T-SA-18.14): "sent" ONLY when the
 *  server reported the mail as sent, an error when it was not, a neutral hint when the outcome
 *  is unknown (a later visit) - always with the resend action. `children` = further actions
 *  on the same button row (e.g. "Continue"). */
function PasswordLinkNote(props: {
  status: PasswordMailStatus | null;
  email: string;
  portalUrl?: string;
  busy: boolean;
  onResend: () => void;
  children?: ReactNode;
}) {
  const { status, email, portalUrl, busy, onResend, children } = props;
  const host = (portalUrl || "").replace(/^https?:\/\//i, "");
  const site = host || t("email.password.signInPage");
  return (
    <>
      {status === "Sent" && (
        <div className="fsub">{t("email.passwordSent", { site: host, email: email || t("about.email") })}</div>
      )}
      {status === "NoInternalSite" && <div className="err">{t("email.password.notSent.noSite")}</div>}
      {status === "SendFailed" && <div className="err">{t("email.password.notSent.failed", { site })}</div>}
      {status === null && <div className="fsub">{t("email.password.unknown", { site })}</div>}
      <div className="fnav">
        <button type="button" className="btn" disabled={busy} onClick={onResend}>
          {t("email.password.resend")}
        </button>
        {children}
      </div>
    </>
  );
}

/** The header icon = the FIRST letter of the agency name (2026-10-02). */
function initials(name: string): string {
  const first = name.trim().split(/\s+/).filter(Boolean)[0];
  return first ? first[0]!.toUpperCase() : "•";
}
